// Janeway GUI Elements — Color Theme Definitions
// Single source of truth for all Janeway ecosystem themes.

// Kein Kreis: `contrast.ts` holt von hier nur einen TYP (`import type`), der beim
// Kompilieren verschwindet. Zur Laufzeit haengt die Kante an der Mathematik, nicht
// umgekehrt — und es gibt sie damit genau einmal.
import { parseHsl, formatHsl, contrastRatio, contrastOverBlend, mischen, luminanz } from "./contrast";

export interface ThemeVars {
  background: string;
  foreground: string;
  card: string;
  "card-foreground": string;
  popover: string;
  "popover-foreground": string;
  primary: string;
  "primary-foreground": string;
  secondary: string;
  "secondary-foreground": string;
  muted: string;
  "muted-foreground": string;
  accent: string;
  "accent-foreground": string;
  destructive: string;
  "destructive-foreground": string;
  border: string;
  input: string;
  ring: string;
  radius: string;
  sidebar: string;
  "sidebar-foreground": string;
  "sidebar-primary": string;
  "sidebar-primary-foreground": string;
  "sidebar-accent": string;
  "sidebar-accent-foreground": string;
  "sidebar-border": string;
  "sidebar-ring": string;
}

export interface Theme {
  id: string;
  label: string;
  /**
   * Anzeigename einer Schrift, die das Theme mitbringt — reine Beschriftung
   * (DesignTab, ThemePreviewPanel, notes, office-tools). **Kein Ladeweg.**
   *
   * Es gibt bewusst KEIN `fontUrl` mehr. Das Feld trug bis 2026-08-10 beim
   * Theme "supabase" eine `fonts.googleapis.com`-Adresse, und
   * `ColorThemeProvider.applyFont` haengte daraus zur Laufzeit ein
   * `<link rel="stylesheet">` in den Kopf: IP-Adresse, Browserkennung und
   * aufrufende Adresse des angemeldeten Nutzers gingen damit unmittelbar an
   * Google LLC (USA) — eine Uebermittlung ohne Rechtsgrundlage nach Kapitel V
   * DSGVO (Art. 44; LG Muenchen I, Urt. v. 20.01.2022 – 3 O 17493/20).
   *
   * Wer einem Theme wieder eine eigene Schrift geben will, liefert sie selbst
   * aus (`next/font` in der App, wie es die Websites tun) und setzt hier nur
   * den Namen. Eine Adresse gehoert hier nie wieder hin.
   */
  fontSans?: string;
  light: ThemeVars;
  dark: ThemeVars;
  /**
   * Die Flaechen der HUELLE — Rahmen und Inhaltsflaeche der Kits von Shell 2.0.
   *
   * Optional und je Modus nur die Abweichung: ohne Eintrag leitet
   * `huellenVars()` beides aus `background`/`foreground`/`card` ab, so wie es
   * `globals.css` bisher allein getan hat. Ein Theme, das hier nichts sagt,
   * verhaelt sich also unveraendert.
   *
   * WARUM NICHT ALS PFLICHTFELDER IN `ThemeVars`: die 43 Themes werden mit dem
   * positionsgebundenen `v()` gebaut — zwei weitere Pflichtwerte waeren 86
   * Aufrufe mit je zwei neuen Argumenten an der richtigen Stelle. Ein optionaler
   * Nachtrag trifft nur die Themes, die wirklich etwas anderes wollen.
   */
  huelle?: {
    light?: Partial<HuellenVars> & { "jw-flaechen"?: "eins" | "zwei" };
    dark?: Partial<HuellenVars> & { "jw-flaechen"?: "eins" | "zwei" };
    /** Gilt fuer beide Modi, sofern keiner es einzeln sagt. */
    flaechen?: "eins" | "zwei";
  };
}

/**
 * Die drei Flaechen, aus denen ein Kit seine Huelle baut.
 *
 * Sie sind KEINE `ThemeVars`: ein Theme MUSS sie nicht fuehren, und wer sie
 * nicht fuehrt, bekommt die Ableitung. Die Namen sind die CSS-Variablen ohne
 * `--`, damit derselbe Schluessel durch Theme, Nutzer-Anpassung und
 * `style.setProperty` laeuft.
 */
export interface HuellenVars {
  /** E0 — der Rahmen: Leiste, Raender, unter „umklammert" auch das Panel. */
  "jw-schale": string;
  "jw-schale-vordergrund": string;
  "jw-schale-gedaempft": string;
  "jw-schale-kante": string;
  "jw-schale-ring": string;
  /**
   * Feldrahmen und Warnfarbe — je Ebene gesucht wie Kante und Ring, weil beide
   * Token des Themes gegen DESSEN Flaechen entworfen sind und auf jeder anderen
   * Ebene durchfielen (gemessen 2026-10-07, 43 Themes x 2 Modi x 2 Stellungen:
   * Feldrahmen auf der Schale 32 von 172 unter 3 : 1, auf der Erhebung 167;
   * Warnfarbe als Text auf der Schale 86, auf der Erhebung 130 unter 4,5 : 1).
   * `-destruktiv-vordergrund` reist mit, weil das Rot auch FLAECHE ist.
   */
  "jw-schale-feld": string;
  "jw-schale-destruktiv": string;
  "jw-schale-destruktiv-vordergrund": string;
  /**
   * Flaeche des Jane-Knopfes („begleiter", `SchwebenderKnopf`): `primary` in die Kartenflaeche
   * der Leiter gemischt („tonal", Lead 2026-11-02). Dunkel immer heller als jeder Grund — die
   * blanke `card` war dort ein Loch (janeway: 4 % Helligkeit vor 18 % Tafel).
   */
  "jw-begleiter-flaeche": string;
  /**
   * Zeichen darauf: `primary`, in der Helligkeit stufenweise zum Vordergrund verschoben (Farbton und
   * Saettigung bleiben), bis es auf der Flaeche 4,5 : 1 traegt (Lead 2026-11-02; 3 : 1 las sich blass).
   */
  "jw-begleiter-zeichen": string;
  /** E1 — die Inhaltsflaeche auf dem Rahmen. */
  "jw-tafel": string;
  "jw-tafel-vordergrund": string;
  "jw-tafel-gedaempft": string;
  "jw-tafel-kante": string;
  "jw-tafel-ring": string;
  "jw-tafel-feld": string;
  "jw-tafel-destruktiv": string;
  "jw-tafel-destruktiv-vordergrund": string;
  /** E2 — was auf der Inhaltsflaeche liegt: Karten, Felder, Kacheln. */
  "jw-karte": string;
  "jw-karte-vordergrund": string;
  "jw-karte-gedaempft": string;
  "jw-karte-kante": string;
  "jw-karte-ring": string;
  "jw-karte-feld": string;
  "jw-karte-destruktiv": string;
  "jw-karte-destruktiv-vordergrund": string;
  /**
   * E3 — was UEBER allem schwebt: Popover, Menue, Dialog.
   *
   * Sie rendern in Portalen am `<body>`, also AUSSERHALB der Huelle, und erben
   * deren Umdefinitionen nicht. Auf einer angehobenen Tafel las sich ein
   * Popover im Theme-Ton deshalb als Loch. Gemeldet hat es niemand — das Modell
   * hat es sichtbar gemacht, und deshalb steht es hier.
   *
   * Es setzt sich von der KARTE ab, nicht von der Tafel: ein Menue geht
   * typischerweise ueber einer Karte auf, und gegen die muss es sich abheben.
   */
  "jw-erhebung": string;
  "jw-erhebung-vordergrund": string;
  "jw-erhebung-gedaempft": string;
  "jw-erhebung-kante": string;
  "jw-erhebung-ring": string;
  "jw-erhebung-feld": string;
  "jw-erhebung-destruktiv": string;
  "jw-erhebung-destruktiv-vordergrund": string;
}

/**
 * Der kleinste Anteil von `basis`, der auf `flaeche` das `ziel` erreicht.
 *
 * Gesucht statt gemischt — und das ist die tragende Entscheidung des ganzen
 * Modells. Ueber alle 43 Themes durchgerechnet (2026-09-18) gibt es KEINEN
 * festen Prozentsatz, der zugleich lesbaren Text und eine sichtbare Klammer
 * traegt: bei Tafel 92 % + Text 70 % fielen 27 Themes ohne sichtbare Klammer
 * an, bei Tafel 88 % blieben 7 Textfehler, bei 84 % waren es 35. Ein globaler
 * Wert muss immer eine der beiden Zusagen brechen — und zwar fuer wechselnde
 * sieben bis fuenfzehn Themes, nie fuer alle.
 *
 * Deshalb wird je Theme gesucht. Themes, die ihr Ziel ohnehin erreichen,
 * behalten ihren eigenen Ton unveraendert: „gedaempft" bleibt dort gedaempft,
 * eine Kante bleibt eine Kante. Angehoben wird nur, wer es noetig hat, und nur
 * so weit, wie es noetig ist.
 *
 * Die Untergrenze 20 % ist die Notbremse: darunter waere `basis` nicht mehr zu
 * erkennen, und dann ist der ehrlichere Wert der Gegenpol selbst.
 */
function sucheTon(basis: string, gegenpol: string, flaechen: string[], ziel: number): string {
  for (let anteil = 100; anteil >= 20; anteil -= 2) {
    const kandidat = anteil === 100 ? basis : mischen(basis, gegenpol, anteil);
    if (!kandidat) break;
    // AUF ALLEN Flaechen, nicht auf der schwierigsten. „Am schwierigsten" nach
    // dem Vordergrund zu bestimmen und dann alle Rollen daran zu messen war
    // mein erster Versuch — er ging fuer Text auf, nicht fuer die Kante:
    // neo-brutalism hell bekam eine Kante, die auf der Karte trug und auf der
    // Tafel bei 1,25 lag. Eine Rolle hat je Flaeche ein eigenes Optimum; wer
    // eine Flaeche auslaesst, laesst sie ganz aus.
    if (flaechen.every((f) => { const r = contrastRatio(kandidat, f); return r !== null && r >= ziel; })) {
      return kandidat;
    }
  }
  return gegenpol;
}

/** Die Zusagen je Rolle. Text nach WCAG 1.4.3, Ring nach 1.4.11, Kante `KANTE_MIN`. */
const ZIEL = { text: 4.5, ring: 3.0, kante: 1.5 } as const;

/**
 * Die Warnfarbe einer Ebene — als SCHRIFT auf ihr und als FLAECHE mit eigenem
 * Vordergrund, beides zugleich.
 *
 * ── WARUM NICHT `sucheTon` ──────────────────────────────────────────────────
 *
 * `sucheTon` mischt Richtung Vordergrund, und der ist auf jeder Ebene grau oder
 * fast grau: aus Rot wird dabei Rosa-Grau, und genau das verbietet der
 * Kommentar an der Rahmen-Regel in `globals.css` seit jeher („eine
 * `color-mix`-Ableitung gegen `--jw-auf-grund` macht aus Rot ein Grau"). Hier
 * wandert deshalb nur die HELLIGKEIT, Farbton und Saettigung bleiben — dieselbe
 * Regel wie `kanteSenken` und `destructiveHellLoesen`/`DunkelLoesen`.
 *
 * ── WARUM BEIDE RICHTUNGEN, NAECHSTE ZUERST ─────────────────────────────────
 *
 * Gesucht wird von der Helligkeit des Themes aus nach beiden Seiten, und der
 * naechste Wert gewinnt: wer schon traegt, bleibt bitgleich, wer nicht traegt,
 * wandert so wenig wie moeglich. Welche Seite das ist, haengt an der Ebene —
 * auf der dunklen Schale heller, auf der hellen Karte dunkler, und bei einer
 * Schale im Mittelgrau (catppuccin hell `hsl(234 12% 42%)`) gibt es nur noch
 * die helle: dort wird das Rot zu einem hellen Rosa (gemessen 2026-10-07:
 * `hsl(347 87% 91%)`), weil ein dunkles Rot auf Mittelgrau rechnerisch keine
 * 4,5 : 1 erreichen KANN.
 *
 * ── WARUM DER VORDERGRUND MITGESUCHT WIRD ───────────────────────────────────
 *
 * Das Rot ist auch die Flaeche des Loeschen-Knopfes (`bg-destructive
 * text-destructive-foreground`, `hover:bg-destructive/90` ueber der Ebene). Eine
 * Helligkeit, die als Schrift traegt und den Knopf unlesbar macht, tauscht einen
 * Fehler gegen einen anderen — der Vordergrund bleibt deshalb, wenn er noch
 * traegt, und wird sonst in seinem Farbton auf die naechste tragende
 * Helligkeit gesucht.
 *
 * `symbole` sind Flaechen, auf denen das Rot nur als Symbol steht (der
 * Loeschen-Knopf beim Ueberfahren, `hover:bg-accent hover:text-destructive`) —
 * dort gilt 3 : 1 nach WCAG 1.4.11. Mit 4,5 : 1 auch auf den Tinten wurde das
 * Rot in catppuccin hell zu reinem Weiss; dann ist es keine Warnfarbe mehr.
 */
function warnfarbeSuchen(
  rot: string,
  rotVordergrund: string,
  flaechen: string[],
  symbole: string[],
): { destruktiv: string; vordergrund: string } {
  const r = parseHsl(rot);
  const v = parseHsl(rotVordergrund);
  if (!r || !v) return { destruktiv: rot, vordergrund: rotVordergrund };
  const guete = (kandidat: string) =>
    Math.min(
      ...flaechen.map((f) => (contrastRatio(kandidat, f) ?? 0) / ZIEL.text),
      ...symbole.map((f) => (contrastRatio(kandidat, f) ?? 0) / ZIEL.ring),
    );
  const traegtAlsFlaeche = (vg: string, kandidat: string) =>
    (contrastRatio(vg, kandidat) ?? 0) >= ZIEL.text &&
    flaechen.every((f) => (contrastOverBlend(vg, kandidat, f, 0.9) ?? 0) >= ZIEL.text);
  const vordergrundFuer = (kandidat: string): string | null => {
    if (traegtAlsFlaeche(rotVordergrund, kandidat)) return rotVordergrund;
    for (let d = 1; d <= 100; d++) {
      for (const richtung of [-1, 1]) {
        const l = Math.round(v.l) + richtung * d;
        if (l < 0 || l > 100) continue;
        const vg = formatHsl({ ...v, l });
        if (traegtAlsFlaeche(vg, kandidat)) return vg;
      }
    }
    return null;
  };
  const verfehlt: { kandidat: string; guete: number }[] = [];
  const start = Math.round(r.l);
  for (let d = 0; d <= 100; d++) {
    for (const richtung of d === 0 ? [0] : [-1, 1]) {
      const l = start + richtung * d;
      if (l < 0 || l > 100) continue;
      const kandidat = d === 0 ? rot : formatHsl({ ...r, l });
      const g = guete(kandidat);
      if (g < 1) {
        verfehlt.push({ kandidat, guete: g });
        continue;
      }
      const vg = vordergrundFuer(kandidat);
      if (vg) return { destruktiv: kandidat, vordergrund: vg };
    }
  }
  // Schafft keine Helligkeit beides, gilt die mit der besten Schrift, auf der
  // der Knopf noch traegt — der Waechter nennt solche Faelle namentlich.
  for (const { kandidat } of verfehlt.sort((a, b) => b.guete - a.guete)) {
    const vg = vordergrundFuer(kandidat);
    if (vg) return { destruktiv: kandidat, vordergrund: vg };
  }
  return { destruktiv: rot, vordergrund: rotVordergrund };
}

/**
 * Die DECKE der Kante — sie darf auf der Flaeche der HUELLE nicht lauter stehen
 * als auf den Flaechen ihres eigenen THEMES.
 *
 * ── WARUM ES SIE GIBT ───────────────────────────────────────────────────────
 *
 * `sucheTon` ist eine BODEN-Suche: sie laeuft von 100 % abwaerts und nimmt den
 * ERSTEN Kandidaten, der das Ziel haelt. Erreicht der rohe Ton das Ziel schon,
 * bleibt er unveraendert stehen — und das ist genau dann falsch, wenn die Leiter
 * eine dunkle Flaeche unter einen Kantenton legt, der fuer eine HELLE Seite
 * entworfen wurde. Die Huelle tut das an zwei Stellen: die Schale ist im hellen
 * Modus das dunkle Ende des Themes, und einflaechig ist die Tafel die Schale.
 *
 * Gemessen am 2026-09-20 ueber 43 Themes x 2 Modi x 2 Schalterstellungen, vor
 * dieser Decke:
 *
 *   Schalenkante  : Schale   Median 2,57   41 von 86 ueber 3,0 : 1   Spitze 10,54
 *   Tafelkante    : Tafel    Median 2,57   41 von 86 ueber 3,0 : 1   Spitze 10,54
 *     (einflaechig; zweiflaechig steht dieselbe Zeile bei Median 1,82 und 1/86)
 *
 * Am Bildpunkt im Showroom, janeway hell einflaechig: `rgb(184,184,184)` auf
 * `rgb(0,0,0)`. Betreiber, 2026-09-20: „das sieht einfach scheisse aus grade mit
 * dieser komischen Linie da."
 *
 * ── DER WIDERSPRUCH, DEN SIE AUFLOEST ───────────────────────────────────────
 *
 * Die Doktrin stand laengst geschrieben, nur nicht im Code. `kanteHeben` sagt
 * „die leiseste Loesung ist hier auch die vollstaendige", und
 * `components/layout/Leistenzier.tsx` beschreibt den Bestand als „hebt jedes
 * `border`-Token auf `KANTE_MIN` (1,5 : 1) und keinen Deut weiter" — was das
 * Anheben tat, das Stehenlassen aber nie. Die Decke macht aus der einseitigen
 * Suche eine BEIDSEITIGE: unter dem Boden wird angehoben, ueber der Decke
 * gesenkt, dazwischen bleibt der Ton des Themes unangetastet.
 *
 * ── WARUM DIE DECKE JE THEME GILT UND NICHT ALS FESTE ZAHL ──────────────────
 *
 * Mein erster Entwurf war eine feste Decke (2,2 : 1, ueber 516 Zeilen
 * ausgesucht: Median 1,99, keine Zeile ueber 3,0, 335 von 516 unveraendert).
 * Sie fixt die Beschwerde — und nimmt `neo-brutalism` sein Merkmal: seine
 * Kante ist reines Schwarz, steht auf der eigenen weissen Karte bei **20,76 : 1**
 * und faellt unter einer festen Decke auf 2,15. Harte schwarze Kanten sind der
 * ganze Zweck dieses Themes; `kanteHeben` nimmt es aus genau diesem Grund
 * ausdruecklich aus (eigener Absatz dort), und eine Decke, die es durch die
 * Hintertuer doch trifft, widerspricht dem.
 *
 * Die Decke ist deshalb die Lautheit ZUHAUSE: der groesste Kontrast, den der
 * `border`-Token des Themes auf einer seiner eigenen sechs Flaechen erreicht
 * (dieselben sechs, gegen die `kanteHeben` beim Bauen sucht). Der Satz dahinter:
 * **in der Huelle so laut wie im Theme, nicht lauter.** Wer eine laute Kante
 * entwirft, behaelt sie; wer eine leise entwirft, bekommt sie nicht durch die
 * Huelle laut zurueck.
 *
 * Beide Regeln nebeneinander, dieselben 172 Zeilen (Tafelkante gegen Tafel und
 * Karte), 2026-09-20:
 *
 *   ungedaempft    min 1,63  Median 1,99  max 20,76   42 von 172 ueber 3,0
 *   feste Decke    min 1,63  Median 1,99  max  2,20    0 von 172
 *   Heimat-Decke   min 1,63  Median 1,98  max 20,76    1 von 172
 *
 * Die eine Zeile ueber 3,0 ist `neo-brutalism` hell zweiflaechig, also genau
 * die, die so bleiben soll. Von den uebrigen 171 weicht keine um mehr als 0,38
 * von der festen Decke ab (`vercel` dunkel einflaechig: 2,57 statt 2,19, weil
 * sein Theme selbst dort 2,57 traegt). Die Heimat-Decke kostet also fast nichts
 * und rettet ein Theme.
 */

/**
 * Senkt eine Kante, bis sie auf KEINER ihrer Flaechen lauter steht als die
 * Decke — und keinen Schritt weiter.
 *
 * Gegenstueck zu `kanteHeben`, mit derselben Mechanik und derselben
 * Begruendung: nur die HELLIGKEIT wandert, Farbton und Saettigung bleiben
 * stehen (75 der 86 `border`-Token sind farbig; wer sie mischt, macht aus der
 * Kante beilaeufig eine andere Farbe).
 *
 * Der BODEN SCHLAEGT DIE DECKE, und das ist kein Randfall: zwischen 1,5 und der
 * Decke des Themes liegt nicht immer eine ganze Helligkeitsstufe. Wo das Fenster
 * leer ist, gilt die leiseste Helligkeit, die den Boden noch traegt — eine
 * Kante, die man nicht sieht, ist schlimmer als eine, die zu laut ist. Gemessen
 * 2026-09-20 ueber 172 Theme/Modus/Stellungs-Zeilen x 4 Rollen: **41 von 688**
 * liegen so, die groesste Ueberschreitung ist `vercel` hell einflaechig mit
 * 2,19 statt 1,70. Der Boden selbst wird in keiner einzigen Zeile gebrochen.
 * Der Waechter dazu kennt diesen Fall namentlich
 * (`src/__tests__/kante-wird-in-der-huelle-nicht-lauter.test.ts`).
 *
 * Gesucht wird von der Ausgangshelligkeit aus nach BEIDEN Seiten, nicht nur
 * abwaerts: welche Richtung leiser ist, haengt daran, ob die Kante heller oder
 * dunkler als ihre Flaeche steht, und beides kommt vor (hell einflaechig steht
 * sie heller, `neo-brutalism` hell zweiflaechig dunkler).
 */
export function kanteSenken(kante: string, flaechen: string[], decke: number): string {
  const k = parseHsl(kante);
  if (!k) return kante;
  const gegen = (l: number) => flaechen.map((f) => contrastRatio(formatHsl({ ...k, l }), f) ?? 0);
  const traegt = (l: number) => Math.min(...gegen(l)) >= ZIEL.kante;
  const lautheit = (l: number) => Math.max(...gegen(l));
  const start = Math.round(k.l);
  if (lautheit(start) <= decke) return kante;
  for (let d = 1; d <= 100; d++) {
    for (const richtung of [-1, 1]) {
      const l = start + richtung * d;
      if (l < 0 || l > 100) continue;
      if (traegt(l) && lautheit(l) <= decke) return formatHsl({ ...k, l });
    }
  }
  let leiseste: number | null = null;
  for (let l = 0; l <= 100; l++) {
    if (!traegt(l)) continue;
    if (leiseste === null || lautheit(l) < lautheit(leiseste)) leiseste = l;
  }
  return leiseste === null ? kante : formatHsl({ ...k, l: leiseste });
}

/**
 * Eine Ebene aus ihrer Flaeche ableiten — vier Rollen, jede an ihrer Zusage
 * gemessen.
 *
 * `gegenueber` ist die Flaeche, gegen die zusaetzlich geprueft wird: eine Ebene
 * traegt oft etwas, das noch einmal darueber liegt (die Tafel traegt Karten),
 * und der hellere der beiden Gruende ist der schwierigere Fall. Wer nur gegen
 * die eigene Flaeche rechnet, bekommt Text, der auf jeder Karte darauf
 * durchfaellt — genau so am 2026-09-18 gemessen, 38 Stellen.
 */
function ebene(
  flaeche: string,
  kandidaten: string[],
  /**
   * `heimat` sind die eigenen Flaechen des Themes — sie liefern die Decke der
   * Kante („in der Huelle so laut wie im Theme, nicht lauter", s. dort).
   */
  quelle: {
    gedaempft: string;
    kante: string;
    ring: string;
    feld: string;
    destruktiv: string;
    destruktivVordergrund: string;
    heimat: string[];
  },
  gegenueber = flaeche,
  /**
   * Die TINTEN, die auf dieser Flaeche liegen — Anteile des Vordergrunds in
   * PROZENT, so wie `globals.css` sie unter „umklammert" mischt
   * (`color-mix(in srgb, var(--jw-auf-grund) N%, transparent)`).
   *
   * ── WARUM ES SIE BRAUCHT ────────────────────────────────────────────────
   *
   * `--muted-foreground` ist `--jw-schale-gedaempft`, gesucht gegen die BLANKE
   * Schale. Gemalt wird es aber auf `--muted` (14 %) und `--accent` (10 %), und
   * jede Tinte frisst Reserve, die nie eingeplant war. Am 2026-09-20 ueber
   * 43 Themes x 2 Modi x 2 Stellungen gerechnet: `bg-muted text-muted-foreground`
   * im Rahmen — die Zaehler-Plakette an jedem Leisteneintrag (`Sidebar.tsx`) —
   * hielt in **158 von 172** Zeilen die 4,5 : 1 nicht (mono hell 2,98, live
   * 3,01). Gegen die Tinte mitgesucht bleiben **2**, beide in catppuccin hell.
   *
   * ── WARUM NUR DER GEDAEMPFTE TON, NICHT KANTE UND RING ──────────────────
   *
   * Beides gemessen, beides verworfen:
   *
   *  - **Ring:** auf einer Tinte steht kein Fokusring, den eine Zusage nennt.
   *    Mitgesucht aendert er 126 Toene und hebt `ring/grund` von 3,00 auf
   *    4,80 — lauter ohne einen einzigen gewonnenen Fall.
   *  - **Kante:** hier laufen zwei Zusagen GEGENEINANDER, und das ist keine
   *    Umsetzungsfrage, sondern Geometrie. Die Tinten liegen zwischen Schale
   *    und Vordergrund; wer 1,5 : 1 gegen die Schale UND gegen die Tinte
   *    verlangt, treibt die Kante von beiden weg — also laut gegen die Schale.
   *    Gemessen als Median von `border` gegen die blanke Schale: heute 1,95,
   *    mit der 14-%-Tinte 2,10, mit allen dreien 2,53. Das ist der Stand VOR
   *    der Decke (Median 2,57), die der Betreiber am 2026-09-20 genau deshalb
   *    verlangt hat („diese komische Linie"), und `kante-wird-in-der-huelle-
   *    nicht-lauter` faellt dabei in 94 bzw. 114 von 688 Rollen.
   *    Die Kante bleibt deshalb an der Flaeche, auf der sie LIEGT. Eine Kante
   *    um eine getoente Plakette ist eine GRENZE, keine Marke auf der Tinte:
   *    sie ist zu sehen, sobald sie sich von EINER Seite abhebt, und
   *    `border` gegen die blanke Schale haelt in allen 172 Zeilen (min 1,55).
   */
  tinten: number[] = [],
): {
  vordergrund: string;
  gedaempft: string;
  kante: string;
  ring: string;
  feld: string;
  destruktiv: string;
  destruktivVordergrund: string;
} {
  const flaechen = flaeche === gegenueber ? [flaeche] : [flaeche, gegenueber];
  // ── DIE EBENE WAEHLT IHREN VORDERGRUND, SIE BEKOMMT IHN NICHT ZUGETEILT ──
  //
  // Normalerweise ist die erste Wahl richtig: eine dunkle Flaeche traegt den
  // hellen Ton. Bei einer Schale im MITTELGRAU stimmt das nicht mehr — dort
  // traegt weder hell noch dunkel gut, und welcher der beiden gewinnt, haengt
  // an der Ebene. catppuccin hell (`hsl(234 12% 42%)`) einflaechig gemessen:
  // der helle Ton kam auf der angehobenen Karte auf 4,27 : 1, der dunkle traegt.
  //
  // Genommen wird der erste Kandidat, der auf ALLEN Flaechen dieser Ebene
  // 4,5 : 1 haelt; schafft es keiner, der mit dem besten schlechtesten Wert.
  // So kippt eine einzelne Ebene um, statt dass das ganze Theme durchfaellt.
  const guete = (k: string) => Math.min(...flaechen.map((f) => contrastRatio(k, f) ?? 0));
  const vordergrund =
    kandidaten.find((k) => guete(k) >= ZIEL.text) ??
    kandidaten.reduce((a, b) => (guete(b) > guete(a) ? b : a));
  // Die Tinten entstehen AUS dem Vordergrund — sie koennen erst stehen, wenn er
  // gewaehlt ist. Ihn seinerseits gegen sie zu waehlen bringt nichts: gemessen
  // kippt kein einziger der 172 Faelle, weil die Tinte mit dem Kandidaten
  // mitwandert.
  const getoent = tinten
    .map((anteil) => mischen(vordergrund, flaeche, anteil))
    .filter((f): f is string => f !== null);
  const warnung = warnfarbeSuchen(quelle.destruktiv, quelle.destruktivVordergrund, flaechen, getoent);
  return {
    vordergrund,
    gedaempft: sucheTon(quelle.gedaempft, vordergrund, [...flaechen, ...getoent], ZIEL.text),
    // Beidseitig: `sucheTon` hebt an, bis der Boden haelt, `kanteSenken` senkt,
    // bis die Decke haelt. Nacheinander und in dieser Reihenfolge, weil die
    // Senkung den Boden kennt und ihn nie unterschreitet — umgekehrt wuerde die
    // Anhebung die Senkung sofort wieder aufheben.
    kante: kanteSenken(
      sucheTon(quelle.kante, vordergrund, flaechen, ZIEL.kante),
      flaechen,
      Math.max(
        ZIEL.kante,
        ...quelle.heimat.map((f) => contrastRatio(quelle.kante, f) ?? 0),
      ),
    ),
    ring: sucheTon(quelle.ring, vordergrund, flaechen, ZIEL.ring),
    // Der Feldrahmen ist BEDIENUNG wie der Ring (1.4.11), keine Trennlinie —
    // deshalb dessen Ziel und dessen Suche, nicht die der Kante. Die Tinten
    // fehlen hier mit Absicht: ein Feld sitzt auf seiner Flaeche, nicht auf
    // einer Toenung (dieselbe Begruendung wie beim Ring oben).
    feld: sucheTon(quelle.feld, vordergrund, flaechen, ZIEL.ring),
    destruktiv: warnung.destruktiv,
    destruktivVordergrund: warnung.vordergrund,
  };
}

/** Untergrenze fuer Janes Zeichen auf seiner Flaeche — 4,5 statt der Grafik-3 : 1, die sich dunkel blass las. */
export const BEGLEITER_ZEICHEN_MIN = 4.5;
/** Mindestabstand der Luminanz zwischen Janes Flaeche und jedem Grund dahinter. */
export const BEGLEITER_FLAECHE_ABSTAND = 0.02;
/**
 * Anteil `primary` in der Kartenflaeche (Lead 2026-11-02). Gemessen ueber 86 Modi x 3
 * Schalterstellungen: 12/22 haelt die meisten Zeichen bei `primary` (4,5 : 1 in 37 Modi; 15/22:
 * 28, 18/26: 19) und laesst nur t3-chat/dunkel weitersuchen (bis 42 %); mit 18 % dunkel waeren es 6.
 */
const BEGLEITER_ANTEIL = { hell: 12, dunkel: 22 } as const;

/**
 * Was fuer dieses Theme und diesen Modus als Huelle gilt — die ganze Leiter.
 *
 * Einstellbar sind nur die beiden FLAECHEN E0 und E1. Alles andere wird
 * abgeleitet und an seiner Zusage gemessen (`ebene`), statt mit einem festen
 * Prozentsatz gemischt zu werden. Spec:
 * `docs/superpowers/specs/2026-09-18-flaechenmodell-design.md`.
 */
export function huellenVars(
  theme: Theme,
  isDark: boolean,
  /**
   * Die Wahl des NUTZERS, falls er eine getroffen hat — sie schlaegt das Theme.
   * Sie reist als gewoehnliche Anpassung (`customOverrides`) mit, weil sie
   * genau dorthin gehoert: eine Entscheidung ueber das Aussehen, die das Theme
   * vorschlaegt und der Nutzer ueberstimmt. Ein eigener Speicherweg daneben
   * waere ein zweiter Weg fuer dieselbe Sache.
   */
  schalter?: "eins" | "zwei",
): HuellenVars {
  const v = isDark ? theme.dark : theme.light;
  const eigen = isDark ? theme.huelle?.dark : theme.huelle?.light;

  // Hell ist das dunkle Ende eines Themes `--foreground`, dunkel `--background`.
  const schale =
    eigen?.["jw-schale"] ??
    (isDark ? v.background : mischen(v.foreground, v.background, 88) ?? v.foreground);

  // DER SCHALTER. `eins` legt die Tafel auf die Schale — Rahmen und Inhalt
  // tragen dieselbe Farbe, getrennt wird nur noch durch die Kante. Betreiber,
  // 2026-09-18: „dass ich im dunklen Modus im Contentbereich nicht grau habe,
  // sondern auch schwarz, und man's nur durch eine Border richtig geil sieht."
  const einflaechig =
    (schalter ?? eigen?.["jw-flaechen"] ?? theme.huelle?.flaechen ?? "zwei") === "eins";
  // Die Tafel hebt sonst nur im dunklen Modus an — hell ist die Karte weiss.
  const tafel =
    eigen?.["jw-tafel"] ??
    (einflaechig ? schale : isDark ? mischen(v.card, v.foreground, 86) ?? v.card : v.card);

  // Eine Flaeche reist mit ihrem Vordergrund: auf der Schale gilt der Gegenpol
  // ihres Modus — hell ist sie dunkel, traegt also den hellen Ton.
  const aufSchale =
    eigen?.["jw-schale-vordergrund"] ?? (isDark ? v.foreground : v.background);

  // ── UND DAS GILT AUCH FUER DEN INHALT, SOBALD ER AUF DER SCHALE LIEGT ────
  // Einflaechig ist die Tafel die Schale — dann traegt sie auch deren
  // Vordergrund, nicht den des Themes. Ich hatte hier zuerst `v.foreground`
  // stehen lassen und damit genau die Regel gebrochen, die drei Zeilen darueber
  // steht: im hellen janeway ergab das dunkle Schrift auf schwarzer Flaeche,
  // **1,06 : 1**, und zwar in 285 von 1118 Paaren. Gemessen, bevor es jemand
  // zu sehen bekam.
  const aufInhalt = einflaechig ? aufSchale : v.foreground;

  // E2 liegt auf E1. Einflaechig muss sie STAERKER absetzen: dann ist sie das
  // einzige, was Inhalt von Rahmen unterscheidet. Angehoben wird Richtung
  // `aufInhalt` — einflaechig ist das der HELLE Ton auf dunkler Schale, auch im
  // hellen Modus. Mit `v.foreground` waere die Karte dort ununterscheidbar
  // geblieben (janeway hell: 1,000).
  const karte = (() => {
    if (!isDark && !einflaechig) return v.card;
    // ── AUCH DIE KARTENFLAECHE WIRD GESUCHT, NICHT GEMISCHT ────────────────
    //
    // Sie steht zwischen zwei Zusagen, die gegeneinander laufen: je hoeher sie
    // liegt, desto besser hebt sie sich von der Tafel ab — und desto schlechter
    // traegt der helle Ton darauf. Bei catppuccin hell (Schale `hsl(234 12%
    // 42%)`, ein MITTELGRAU) ist das Fenster leer: einflaechig kam der beste
    // von beiden Polen auf 4,27 : 1, gleich welcher.
    //
    // In so einem Fall gewinnt die LESBARKEIT. Die Abhebung darf ausfallen, die
    // Kante traegt sie dann allein — das ist ohnehin die Idee des einflaechigen
    // Modus („man sieht's nur durch eine Border"). Ein unlesbarer Text hat
    // dagegen keinen Ersatz.
    const start = einflaechig ? 89 : 96;
    for (let anteil = start; anteil <= 100; anteil += 1) {
      const kandidat = anteil === 100 ? tafel : mischen(tafel, aufInhalt, anteil);
      if (!kandidat) break;
      const traegt = [aufInhalt, aufInhalt === v.foreground ? v.background : v.foreground]
        .some((ton) => (contrastRatio(ton, kandidat) ?? 0) >= ZIEL.text);
      if (traegt) return kandidat;
    }
    return tafel;
  })();
  const quelle = {
    gedaempft: v["muted-foreground"],
    kante: v.border,
    ring: v.ring,
    feld: v.input,
    destruktiv: v.destructive,
    destruktivVordergrund: v["destructive-foreground"],
    // Die sechs Flaechen des Themes selbst — dieselbe Liste, gegen die
    // `kanteHeben` den `border`-Token beim Bauen anhebt. Sie liefert die Decke
    // der Kante; beide Enden derselben Zusage stehen damit an derselben Menge.
    heimat: [v.background, v.card, v.secondary, v.muted, v.accent, v.sidebar],
  };

  // Der Gegenpol als zweiter Kandidat — er kommt nur zum Zug, wenn der erste
  // nicht traegt (Mittelgrau-Schalen).
  const gegenpol = aufSchale === v.foreground ? v.background : v.foreground;
  // Die Schale ist die EINZIGE Ebene mit Tinten: `globals.css` mischt `--accent`
  // (10 %) und `--muted` (14 %) nur im Block „umklammert". `--secondary` (20 %)
  // steht bewusst NICHT hier — es traegt immer `--secondary-foreground`, nie den
  // gedaempften Ton (gezaehlt: 0 von 20 `bg-secondary`-Stellen), und mitgesucht
  // liesse es den gedaempften Ton in 52 statt 10 von 172 Zeilen ganz auf den
  // Vordergrund fallen. Bindend ist heute ohnehin nur die 14 %; die 10 % stehen
  // hier, weil `text-muted-foreground` auf `bg-accent` 71-mal vorkommt und die
  // Liste sagen soll, was die Stilvorlage malt — nicht, was gerade knapp reicht.
  const e0 = ebene(schale, [aufSchale, gegenpol], quelle, undefined, [10, 14]);
  // E1 wird ZUSAETZLICH gegen E2 geprueft: die Karte liegt darueber und ist der
  // hellere, also schwierigere Grund. Wer nur gegen die Tafel rechnet, bekommt
  // Text, der auf jeder Karte darauf durchfaellt — 38 Stellen, gemessen.
  const inhaltsPole = [aufInhalt, aufInhalt === v.foreground ? v.background : v.foreground];
  const e1 = ebene(tafel, inhaltsPole, quelle, karte);
  const e2 = ebene(karte, inhaltsPole, quelle);
  // E3 — eine Stufe ueber der Karte, in dieselbe Richtung. Im hellen Modus ist
  // die Karte meist schon weiss; dann bleibt die Erhebung dort und traegt ihre
  // Trennung ueber die Kante und den Schatten, nicht ueber die Flaeche.
  const erhebung = mischen(karte, inhaltsPole[0]!, 94) ?? karte;
  const e3 = ebene(erhebung, inhaltsPole, quelle);

  // ── JANES SCHEIBE: TONAL ──────────────────────────────────────────────────
  // Basis ist die Karte der LEITER, nicht `v.card`: `card` ist im Dunkeln der tiefste Ton des
  // Themes, die Tafel liegt hoeher — mit `v.card` als Basis blieb die Flaeche in 30 von 86 Modi
  // dunkler als der Grund. Hell/dunkel entscheidet die gemalte TAFEL, nicht der Modus: einflaechig
  // liegt der Inhalt auch im hellen Modus auf der dunklen Schale. Grund ist, was hinter dem Knopf
  // gemalt wird — Tafel und Schale; dunkel liegt `background` dort ohnehin auf der Schale.
  const auf = (f: string) => luminanz(f) ?? 0;
  const grundDunkel = auf(tafel) < auf(aufInhalt);
  const gruende = isDark ? [tafel, schale, v.background] : [tafel, schale];
  const abstandOk = (f: string) =>
    gruende.every((g) => {
      const d = auf(f) - auf(g);
      return grundDunkel ? d >= BEGLEITER_FLAECHE_ABSTAND : Math.abs(d) >= BEGLEITER_FLAECHE_ABSTAND;
    });
  // ── DAS ZEICHEN BLEIBT FARBIG ─────────────────────────────────────────────
  // 4,5 : 1 (Lead 2026-11-02, bei 3 : 1 las es sich dunkel blass). Erst wird nur die HELLIGKEIT von
  // `primary` zum Vordergrund der Flaeche verschoben, Farbton und Saettigung bleiben — ein Mix mit
  // dem Vordergrund nahm ihnen beides (claude dunkel kippte von Farbton 18 auf 40, fast grau).
  // Reicht auch das nicht, wird zum Vordergrund gemischt. `null`: auf dieser Flaeche geht es nicht.
  const zeichenAuf = (flaeche: string): string | null => {
    const traegt = (k: string | null): k is string =>
      !!k && (contrastRatio(k, flaeche) ?? 0) >= BEGLEITER_ZEICHEN_MIN;
    if (traegt(v.primary)) return v.primary;
    const p = parseHsl(v.primary);
    if (p) {
      const schritt = auf(aufInhalt) > auf(flaeche) ? 1 : -1;
      for (let l = p.l + schritt; l >= 0 && l <= 100; l += schritt) {
        const k = formatHsl({ ...p, l });
        if (traegt(k)) return k;
      }
    }
    for (let anteil = 99; anteil >= 0; anteil -= 1) {
      const k = mischen(v.primary, aufInhalt, anteil);
      if (traegt(k)) return k;
    }
    return null;
  };
  // Reicht der Anteil nicht, steigt er (t3-chat/dunkel: 42 %). Ist `primary` selbst dunkel
  // (janeway hell = Schwarz, einflaechig auf schwarzer Schale), kann kein Anteil aufhellen — dann
  // hebt sich erst die Basis zum Vordergrund an, getoent wird danach wie sonst. Traegt auf einer
  // Flaeche kein Zeichen 4,5 : 1, wird zuerst WENIGER gemischt: eine helle `primary` (catppuccin
  // dunkel, 81 % L) hob die Flaeche bei 22 % so weit, dass selbst der Vordergrund nur 4,33 hielt.
  const { flaeche: begleiterFlaeche, zeichen: begleiterZeichen } = (() => {
    const start = grundDunkel ? BEGLEITER_ANTEIL.dunkel : BEGLEITER_ANTEIL.hell;
    const anteile: number[] = [];
    for (let a = start; a >= 6; a -= 2) anteile.push(a);
    for (let a = start + 2; a <= 60; a += 2) anteile.push(a);
    for (let hub = 0; hub <= 40; hub += 2) {
      const basis = hub === 0 ? karte : mischen(aufInhalt, karte, hub) ?? karte;
      for (const anteil of anteile) {
        const kandidat = mischen(v.primary, basis, anteil) ?? basis;
        if (!abstandOk(kandidat)) continue;
        const zeichen = zeichenAuf(kandidat);
        if (zeichen) return { flaeche: kandidat, zeichen };
      }
    }
    const notfall = mischen(v.primary, karte, start) ?? karte;
    return { flaeche: notfall, zeichen: zeichenAuf(notfall) ?? aufInhalt };
  })();

  const abgeleitet: HuellenVars = {
    "jw-begleiter-flaeche": begleiterFlaeche,
    "jw-begleiter-zeichen": begleiterZeichen,
    "jw-schale": schale,
    "jw-schale-vordergrund": e0.vordergrund,
    "jw-schale-gedaempft": e0.gedaempft,
    "jw-schale-kante": e0.kante,
    "jw-schale-ring": e0.ring,
    "jw-schale-feld": e0.feld,
    "jw-schale-destruktiv": e0.destruktiv,
    "jw-schale-destruktiv-vordergrund": e0.destruktivVordergrund,
    "jw-tafel": tafel,
    "jw-tafel-vordergrund": e1.vordergrund,
    "jw-tafel-gedaempft": e1.gedaempft,
    "jw-tafel-kante": e1.kante,
    "jw-tafel-ring": e1.ring,
    "jw-tafel-feld": e1.feld,
    "jw-tafel-destruktiv": e1.destruktiv,
    "jw-tafel-destruktiv-vordergrund": e1.destruktivVordergrund,
    "jw-karte": karte,
    "jw-karte-vordergrund": e2.vordergrund,
    "jw-karte-gedaempft": e2.gedaempft,
    "jw-karte-kante": e2.kante,
    "jw-karte-ring": e2.ring,
    "jw-karte-feld": e2.feld,
    "jw-karte-destruktiv": e2.destruktiv,
    "jw-karte-destruktiv-vordergrund": e2.destruktivVordergrund,
    "jw-erhebung": erhebung,
    "jw-erhebung-vordergrund": e3.vordergrund,
    "jw-erhebung-gedaempft": e3.gedaempft,
    "jw-erhebung-kante": e3.kante,
    "jw-erhebung-ring": e3.ring,
    "jw-erhebung-feld": e3.feld,
    "jw-erhebung-destruktiv": e3.destruktiv,
    "jw-erhebung-destruktiv-vordergrund": e3.destruktivVordergrund,
  };
  // Was ein Theme ausdruecklich setzt, gewinnt. `jw-flaechen` ist keine Farbe
  // und faellt beim Spread heraus — es hat oben schon gewirkt.
  const { "jw-flaechen": _schalter, ...gesetzt } = eigen ?? {};
  return { ...abgeleitet, ...gesetzt };
}

/**
 * Das Theme, wie es der NUTZER sieht — seine Farb-Anpassungen in den Werten des
 * aktiven Modus, damit `huellenVars()` aus ihnen rechnet und nicht am Theme
 * vorbei.
 *
 * Bis 0.555.1 lief die Leiter nur aus dem Theme: wer im Design-Reiter ein
 * eigenes Rot, eine eigene Kante oder einen eigenen Feldrahmen setzte, sah sie
 * unter „umklammert" im Rahmen und auf der Inhaltskarte nicht — dort gilt die
 * Rolle der Leiter, und die kannte die Anpassung nicht. Seit Feldrahmen und
 * Warnfarbe Leiter-Rollen sind (2026-10-07), traf das auch `input` und
 * `destructive`.
 *
 * Gemischt werden nur Schluessel, die das Theme als Farbe FUEHRT und deren Wert
 * eine lesbare `hsl()`-Farbe ist. Die Huellen-Schalter (`jw-flaechen`,
 * `jw-huelle-*`) und gesetzte Leiter-Toene (`jw-schale` …) sind keine
 * Theme-Token und gelten wie bisher OBENDRAUF; `radius` ist keine Farbe. Der
 * Modus, der nicht aktiv ist, bleibt unberuehrt — die Anpassungen liegen je
 * Modus getrennt.
 *
 * EIN Ort fuer drei Aufrufer: `ColorThemeProvider`, `theme-ssr.ts` und die
 * Vorschau (`inhaltskartenVars`/`DesignTab`). Drei Kopien liefen auseinander,
 * und dann malte der Erstpaint etwas anderes als die Hydration.
 */
export function mitAnpassungen(
  theme: Theme,
  isDark: boolean,
  eigene: Partial<Record<string, string | undefined>> | undefined,
): Theme {
  if (!eigene) return theme;
  const modus = isDark ? theme.dark : theme.light;
  const gemischt: Record<string, string> = { ...(modus as unknown as Record<string, string>) };
  let geaendert = false;
  for (const [schluessel, wert] of Object.entries(eigene)) {
    if (wert === undefined || !(schluessel in modus) || !parseHsl(wert)) continue;
    if (gemischt[schluessel] === wert) continue;
    gemischt[schluessel] = wert;
    geaendert = true;
  }
  if (!geaendert) return theme;
  const vars = gemischt as unknown as ThemeVars;
  return isDark ? { ...theme, dark: vars } : { ...theme, light: vars };
}

/**
 * Die kleinste Kante, die noch als Kante durchgeht — 1,5 : 1 gegen die Flaeche,
 * auf der sie liegt.
 *
 * WARUM nicht 3 : 1 (WCAG 1.4.11): das gilt fuer Bedienelemente, deren Grenze
 * die Bedienbarkeit traegt. Eine Trennlinie zwischen zwei Flaechen ist Struktur,
 * kein Bedienelement; 3 : 1 waere dort ein schwarzer Strich. Fuer Feldrahmen und
 * Fokusring steht deshalb weiter 3,0 in `CONTRAST_PAIRS` — die sind Bedienung.
 */
const KANTE_MIN = 1.5;

/**
 * Hebt die Kante eines Themes so weit an, bis sie auf JEDER seiner Flaechen zu
 * sehen ist — und keinen Schritt weiter.
 *
 * WARUM ES DAS GIBT (gemessen 2026-08-19 ueber alle 43 Themes x hell/dunkel x 6
 * Flaechen = 516 Faelle): der rohe `border`-Token trug 1,5 : 1 in **30 von 516**
 * Faellen. Auf `secondary`, `muted` und `accent` in genau EINEM von 86. Die Suite
 * hat deshalb jahrelang zwei Sorten Kanten nebeneinander gehabt — die 102 Stellen
 * mit `.jw-linie` (sichtbar) und 1876 mit `border-border` (auf 78 von 86
 * Oberflaechen nicht zu sehen). Beides direkt nebeneinander in derselben Huelle:
 * Kopfzeile gegen Seitenleiste.
 *
 * WARUM NUR DIE HELLIGKEIT WANDERT: Farbton und Saettigung sind das, was die
 * Kante zum Theme gehoeren laesst — 75 der 86 `border`-Token sind farbig. Der
 * frueher gewaehlte Weg (neutrales `foreground` 30 %) machte jede Kante grau und
 * nahm damit 43 Themes dasselbe Merkmal weg. Hier bleiben H und S unangetastet.
 *
 * WARUM MINIMAL UND NICHT MIT FESTEM ANTEIL: eine feste Beimischung muss sich am
 * schlimmsten Theme ausrichten und macht alle anderen unnoetig laut. Gemessen:
 * fester Anteil 35 % ergibt Median 2,76 und Spitzen bis 5,04; die minimale
 * Hebung ergibt Median **1,72** bei Spitze 2,50 — beide 516/516. Die leiseste
 * Loesung ist hier auch die vollstaendige.
 *
 * `neo-brutalism` faellt bewusst aus dem Rahmen: sein `border` ist reines
 * Schwarz und traegt damit schon ~20 : 1. Es wird NICHT angefasst — harte
 * schwarze Kanten sind der ganze Zweck dieses Themes.
 *
 * Die Suche darf binaer sein: die Bedingung ist entlang des Wegs zum Vordergrund
 * monoton (in allen 86 Faellen nachgerechnet, `themeKante.test.ts`).
 */
export function kanteHeben(roh: string, vordergrund: string, flaechen: string[]): string {
  const a = parseHsl(roh);
  const f = parseHsl(vordergrund);
  if (!a || !f) return roh;
  const traegt = (l: number) => {
    const wert = formatHsl({ ...a, l });
    return flaechen.every((fl) => (contrastRatio(wert, fl) ?? 0) >= KANTE_MIN);
  };
  let lo = Math.round(a.l);
  if (traegt(lo)) return formatHsl({ ...a, l: lo });
  let hi = Math.round(f.l);
  while (Math.abs(hi - lo) > 1) {
    const m = Math.round((lo + hi) / 2);
    if (traegt(m)) hi = m;
    else lo = m;
  }
  return formatHsl({ ...a, l: traegt(lo) ? lo : hi });
}

/**
 * Loest `destructive` UND `destructive-foreground` GEMEINSAM fuer den dunklen
 * Modus eines Themes — NIE fuer hell, das dort auf `background`/`card`/`accent`
 * schon besteht (Ausnahme: `muted`, siehe unten, bewusst nicht mitgeloest).
 *
 * WARUM GEMEINSAM UND NICHT NACHEINANDER: der Vordergrund haengt vom Ergebnis der
 * Flaeche ab (er muss GEGEN das neue Rot bestehen), destructive selbst kennt keine
 * Abhaengigkeit vom Vordergrund. Die Reihenfolge ist deshalb erzwungen: erst die
 * TEXT-Richtung (1.4.3, 4,5:1 gegen `card`/`background`/`muted`), danach die
 * FLAECHEN-Richtung (Vordergrund gegen die NEUE Flaeche, statisch UND im
 * `hover:bg-destructive/90`-Blend ueber `card` und `background`, `Button.tsx`).
 *
 * WARUM NUR DIE HELLIGKEIT WANDERT (Text-Richtung): wie `kanteHeben` — Farbton
 * und Saettigung bleiben stehen, sonst wird aus Rot beilaeufig Rosa oder Orange.
 * Bisektion Richtung `dark.foreground` (im Dunkelmodus immer die hellste Flaeche
 * des Themes) — das garantiert einen Wert, der alle drei Flaechen schlaegt, ohne
 * dass irgendwo eine feste obere Schranke geraten werden muss.
 *
 * WARUM DER VORDERGRUND GRAU BLEIBT UND NICHT GEFAERBT WIRD: Praezedenz in dieser
 * Datei — nord/catppuccin/cyberpunk/neo-brutalism dunkeln `destructiveFg` schon
 * VOR dieser Funktion ab, immer achromatisch. Gesucht wird die HELLSTE graue
 * Vordergrundfarbe, die noch besteht (naeher am urspruenglich fast-weissen
 * Standard ist weniger Abweichung) — nicht Schwarz um jeden Preis.
 *
 * WARUM DIE TEXT-HELLIGKEIT MANCHMAL WEITER STEIGT ALS FUER DEN TEXT NOETIG:
 * Grenzfall `violet` — bei der minimalen Text-Helligkeit (59 %) bestand KEIN
 * einziger Grauton alle drei Flaechen-Pruefungen gleichzeitig (Schwarz verfehlte
 * den Hover ueber `background` um 0,003 : 1). Eine Flaeche, die nur die
 * TEXT-Richtung erfuellt und dann keinen tragfaehigen Vordergrund mehr zulaesst,
 * ist kein Fix. Die Funktion geht deshalb, wenn noetig, ueber das Text-Minimum
 * hinaus: `l` steigt Grad fuer Grad weiter, bis ZUGLEICH ein Grauton fuer die
 * Flaechen-Richtung existiert — beide Richtungen muessen am Ende bestehen, nicht
 * nur die zuerst geloeste.
 *
 * Aendert NICHTS, wenn ein Theme schon besteht — die 2 von 43 (catppuccin,
 * cyberpunk), deren `destructive` schon traegt UND deren vorhandener
 * `destructive-foreground` auch die Flaechen-Richtung schon schlaegt, bleiben
 * bitidentisch. Ein vorhandener Vordergrund wird nur ersetzt, wenn er es NICHT
 * mehr tut — kein Diff ohne Grund.
 *
 * Gemessen ueber alle 43 Themes vor dieser Funktion: 41 fielen im Dunkelmodus als
 * TEXT durch (2,47:1 bis 4,06:1), vier weitere (auch die zwei text-bestehenden)
 * fielen im Hover-Blend der Flaeche durch. Danach: keines mehr.
 */
export function destructiveDunkelLoesen(
  dark: Pick<ThemeVars, "destructive" | "destructive-foreground" | "card" | "background" | "muted" | "foreground">,
): Pick<ThemeVars, "destructive" | "destructive-foreground"> {
  const roh = parseHsl(dark.destructive);
  if (!roh) {
    return { destructive: dark.destructive, "destructive-foreground": dark["destructive-foreground"] };
  }

  const flaechenText = [dark.card, dark.background, dark.muted];
  const traegtAlsText = (l: number) => {
    const wert = formatHsl({ ...roh, l });
    return flaechenText.every((f) => (contrastRatio(wert, f) ?? 0) >= 4.5);
  };

  const traegtAlsFlaeche = (fgWert: string, destrWert: string) => {
    const statisch = contrastRatio(fgWert, destrWert) ?? 0;
    const hoverCard = contrastOverBlend(fgWert, destrWert, dark.card, 0.9) ?? 0;
    const hoverBg = contrastOverBlend(fgWert, destrWert, dark.background, 0.9) ?? 0;
    return statisch >= 4.5 && hoverCard >= 4.5 && hoverBg >= 4.5;
  };

  const rohFg = parseHsl(dark["destructive-foreground"]);
  const basisFg = rohFg ?? { h: 0, s: 0, l: 98 };

  /** Hellste graue Vordergrundfarbe, die bei dieser Flaechen-Helligkeit besteht — oder `null`. */
  const besteFlaeche = (destrWert: string): number | null => {
    let beste: number | null = null;
    for (let l = 0; l <= 100; l++) {
      if (traegtAlsFlaeche(formatHsl({ h: basisFg.h, s: basisFg.s, l }), destrWert)) beste = l;
      else if (beste !== null) break;
    }
    return beste;
  };

  let l = roh.l;
  if (!traegtAlsText(l)) {
    const vg = parseHsl(dark.foreground);
    let lo = Math.round(roh.l);
    let hi = vg && vg.l > lo ? Math.round(vg.l) : 100;
    while (Math.abs(hi - lo) > 1) {
      const m = Math.round((lo + hi) / 2);
      if (traegtAlsText(m)) hi = m;
      else lo = m;
    }
    l = traegtAlsText(lo) ? lo : hi;
  }

  let destructive = formatHsl({ ...roh, l });
  let destructiveForeground = dark["destructive-foreground"];

  if (!traegtAlsFlaeche(destructiveForeground, destructive)) {
    let fgL = besteFlaeche(destructive);
    while (fgL === null && l < 100) {
      l += 1;
      destructive = formatHsl({ ...roh, l });
      fgL = besteFlaeche(destructive);
    }
    if (fgL !== null) destructiveForeground = formatHsl({ h: basisFg.h, s: basisFg.s, l: fgL });
  }

  return { destructive, "destructive-foreground": destructiveForeground };
}

/**
 * Die Schwester zu `destructiveDunkelLoesen` — HELL, und damit die Gegenrichtung.
 *
 * Gemessen (vor dieser Funktion, alle 43 Themes hell): `destructive` als Text auf
 * `muted` fiel bei ~30 Themes knapp unter 4,5:1 (3,86–4,49:1), UND
 * `hover:bg-destructive/90` fiel bei fast allen 43 auf ~4,10–4,15:1 — die 10 %
 * beigemischte, fast weiße Fläche hellt das Rot auf.
 *
 * WARUM DIE TEXT-RICHTUNG ALLEIN EINFACH IST: sie ist monoton — dunkler ist gegen
 * `card`/`background`/`muted` (alle nahe Weiß) IMMER besser, ohne Ausnahme
 * (nachgerechnet, `janeway/light`: L=51 % bis L=1 % steigt durchgehend). Bisektion
 * Richtung 0 reicht.
 *
 * WARUM DIE FLAECHEN-RICHTUNG DOCH EIN ZWEITER SCHRITT IST: bei den meisten Themes
 * ist `destructive-foreground` fast weiß (Standard `hsl(0 0% 98%)`) — dann hilft
 * dieselbe Bewegung auch der Fläche, und nichts weiter ist nötig. Aber vier Themes
 * (nord, catppuccin, cyberpunk, neo-brutalism) haben ihr `destructive` schon ROH so
 * hell/gesättigt, dass `v()` dort auch im HELLEN Modus einen dunklen Vordergrund
 * braucht (`hsl(0 0% 6–11%)`). Wird `destructive` jetzt für die Text-Richtung
 * abgedunkelt, rutscht es dem eigenen dunklen Vordergrund entgegen — bei `nord`
 * kollabierte der Flächen-Kontrast so auf 1,37:1 (`destructiveFg` L=7 %, `destructive`
 * bei der text-minimalen Helligkeit ebenfalls um L≈21 %, praktisch gleich hell). Die
 * Funktion prüft die Fläche deshalb SEPARAT nach der Text-Lösung und sucht nötigenfalls
 * einen neuen Vordergrund — von Weiß abwärts, weil das bei einem jetzt dunkleren
 * `destructive` fast immer die bessere Wahl ist als noch dunkler zu werden.
 */
export function destructiveHellLoesen(
  light: Pick<ThemeVars, "destructive" | "destructive-foreground" | "card" | "background" | "muted" | "foreground">,
): Pick<ThemeVars, "destructive" | "destructive-foreground"> {
  const roh = parseHsl(light.destructive);
  if (!roh) {
    return { destructive: light.destructive, "destructive-foreground": light["destructive-foreground"] };
  }

  const traegtAlsText = (l: number) => {
    const wert = formatHsl({ ...roh, l });
    return [light.card, light.background, light.muted].every((f) => (contrastRatio(wert, f) ?? 0) >= 4.5);
  };

  const traegtAlsFlaeche = (fgWert: string, destrWert: string) =>
    (contrastRatio(fgWert, destrWert) ?? 0) >= 4.5 &&
    (contrastOverBlend(fgWert, destrWert, light.card, 0.9) ?? 0) >= 4.5 &&
    (contrastOverBlend(fgWert, destrWert, light.background, 0.9) ?? 0) >= 4.5;

  /** Hellste graue Vordergrundfarbe (von Weiß abwärts), die bei dieser Fläche besteht. */
  const besteFlaeche = (destrWert: string, basisFg: { h: number; s: number }): number | null => {
    for (let l = 100; l >= 0; l--) {
      if (traegtAlsFlaeche(formatHsl({ h: basisFg.h, s: basisFg.s, l }), destrWert)) return l;
    }
    return null;
  };

  let l = roh.l;
  if (!traegtAlsText(l)) {
    let lo = 0;
    let hi = Math.round(roh.l);
    while (Math.abs(hi - lo) > 1) {
      const m = Math.round((lo + hi) / 2);
      if (traegtAlsText(m)) lo = m;
      else hi = m;
    }
    l = traegtAlsText(lo) ? lo : hi;
  }

  let destructive = formatHsl({ ...roh, l });
  let destructiveForeground = light["destructive-foreground"];

  if (!traegtAlsFlaeche(destructiveForeground, destructive)) {
    const rohFg = parseHsl(destructiveForeground);
    const basisFg = rohFg ? { h: rohFg.h, s: rohFg.s } : { h: 0, s: 0 };
    let fgL = besteFlaeche(destructive, basisFg);
    while (fgL === null && l > 0) {
      l -= 1;
      destructive = formatHsl({ ...roh, l });
      fgL = besteFlaeche(destructive, basisFg);
    }
    if (fgL !== null) destructiveForeground = formatHsl({ h: basisFg.h, s: basisFg.s, l: fgL });
  }

  return { destructive, "destructive-foreground": destructiveForeground };
}

function v(
  bg: string, fg: string, card: string, cardFg: string,
  primary: string, primaryFg: string,
  secondary: string, secondaryFg: string,
  muted: string, mutedFg: string,
  accent: string, accentFg: string,
  destructive: string,
  border: string, input: string, ring: string,
  radius: string,
  sidebar: string, sidebarFg: string,
  sidebarPrimary: string, sidebarPrimaryFg: string,
  sidebarAccent: string, sidebarAccentFg: string,
  sidebarBorder: string, sidebarRing: string,
  // Standardmaessig helles Weiss. Themes mit heller Warnflaeche (catppuccin, cyberpunk,
  // neo-brutalism, nord) uebergeben einen dunklen Wert, sonst faellt der Text durch (WCAG 1.4.3).
  destructiveFg: string = "hsl(0 0% 98%)",
): ThemeVars {
  return {
    background: bg, foreground: fg,
    card, "card-foreground": cardFg,
    popover: card, "popover-foreground": cardFg,
    primary, "primary-foreground": primaryFg,
    secondary, "secondary-foreground": secondaryFg,
    muted, "muted-foreground": mutedFg,
    accent, "accent-foreground": accentFg,
    destructive, "destructive-foreground": destructiveFg,
    // Die Kante wird gehoben, nicht abgeschrieben. Die Flaechenliste ist der
    // Punkt: eine Linie, die nur gegen `background` geprueft wird, verschwindet
    // auf der Karte und in jeder gedaempften Flaeche — genau das war der Zustand.
    border: kanteHeben(border, fg, [bg, card, secondary, muted, accent, sidebar]),
    input, ring, radius,
    sidebar, "sidebar-foreground": sidebarFg,
    "sidebar-primary": sidebarPrimary, "sidebar-primary-foreground": sidebarPrimaryFg,
    "sidebar-accent": sidebarAccent, "sidebar-accent-foreground": sidebarAccentFg,
    // Auch die Kante der Seitenleiste — sonst zeigt die Theme-Vorschau eine
    // Leiste ohne Rand, waehrend die echte einen hat.
    "sidebar-border": kanteHeben(sidebarBorder, sidebarFg, [sidebar, sidebarAccent]),
    "sidebar-ring": sidebarRing,
  };
}

const themesRoh: Theme[] = [
  // ── Neutral / Janeway ──────────────────────────────────────────────────────
  {
    id: "janeway", label: "Janeway",
    // PURES SCHWARZ ALS RAHMEN, IN BEIDEN MODI — Betreiber, 2026-09-18:
    // „beispielsweise im Janeway-Theme soll diese komplett schwarz sein, im
    // Hellen und Dunklen."
    //
    // Die Ableitung kaeme hier nicht hin: hell mischt sie `--foreground`
    // (hsl(0 0% 4%)) mit 12 % `--background` und landet bei rund hsl(0 0% 15%)
    // — ein sehr dunkles Grau, nicht Schwarz. Dunkel trifft sie Schwarz von
    // selbst, weil `--background` dort bereits hsl(0 0% 0%) ist; die Zeile
    // steht trotzdem da, weil sie eine ABSICHT festhaelt und nicht einen
    // Zufall: wer eines Tages den dunklen Grund anhebt, soll den Rahmen nicht
    // stillschweigend mitheben.
    huelle: {
      light: { "jw-schale": "hsl(0 0% 0%)" },
      dark: { "jw-schale": "hsl(0 0% 0%)" },
    },
    light: v("hsl(0 0% 98%)", "hsl(0 0% 4%)", "hsl(0 0% 100%)", "hsl(0 0% 4%)", "hsl(0 0% 9%)", "hsl(0 0% 98%)", "hsl(0 0% 92%)", "hsl(0 0% 9%)", "hsl(0 0% 92%)", "hsl(0 0% 40%)", "hsl(0 0% 88%)", "hsl(0 0% 9%)", "hsl(0 72% 51%)", "hsl(0 0% 82%)", "hsl(0 0% 57%)", "hsl(0 0% 4%)", "0.5rem", "hsl(0 0% 98%)", "hsl(0 0% 4%)", "hsl(0 0% 9%)", "hsl(0 0% 98%)", "hsl(0 0% 88%)", "hsl(0 0% 9%)", "hsl(0 0% 82%)", "hsl(0 0% 4%)"),
    dark: v("hsl(0 0% 0%)", "hsl(0 0% 98%)", "hsl(0 0% 5%)", "hsl(0 0% 98%)", "hsl(0 0% 98%)", "hsl(0 0% 9%)", "hsl(0 0% 12%)", "hsl(0 0% 98%)", "hsl(0 0% 10%)", "hsl(0 0% 55%)", "hsl(0 0% 14%)", "hsl(0 0% 98%)", "hsl(0 72% 51%)", "hsl(0 0% 13%)", "hsl(0 0% 38%)", "hsl(0 0% 37%)", "0.5rem", "hsl(0 0% 7%)", "hsl(0 0% 98%)", "hsl(0 0% 98%)", "hsl(0 0% 9%)", "hsl(0 0% 14%)", "hsl(0 0% 98%)", "hsl(0 0% 11%)", "hsl(0 0% 35%)"),
  },
  {
    id: "vercel", label: "Vercel",
    light: v("hsl(0 0% 99%)", "hsl(0 0% 0%)", "hsl(0 0% 99%)", "hsl(0 0% 0%)", "hsl(0 0% 0%)", "hsl(0 0% 100%)", "hsl(0 0% 94%)", "hsl(0 0% 0%)", "hsl(0 0% 97%)", "hsl(0 0% 44%)", "hsl(0 0% 94%)", "hsl(0 0% 0%)", "hsl(0 62% 50%)", "hsl(0 0% 90%)", "hsl(0 0% 57%)", "hsl(0 0% 57%)", "0.5rem", "hsl(0 0% 97%)", "hsl(0 0% 0%)", "hsl(0 0% 0%)", "hsl(0 0% 100%)", "hsl(0 0% 94%)", "hsl(0 0% 0%)", "hsl(0 0% 90%)", "hsl(0 0% 60%)"),
    dark: v("hsl(0 0% 0%)", "hsl(0 0% 100%)", "hsl(0 0% 5%)", "hsl(0 0% 100%)", "hsl(0 0% 100%)", "hsl(0 0% 0%)", "hsl(0 0% 15%)", "hsl(0 0% 100%)", "hsl(0 0% 13%)", "hsl(0 0% 55%)", "hsl(0 0% 20%)", "hsl(0 0% 100%)", "hsl(0 62% 50%)", "hsl(0 0% 14%)", "hsl(0 0% 38%)", "hsl(0 0% 40%)", "0.5rem", "hsl(0 0% 3%)", "hsl(0 0% 100%)", "hsl(0 0% 100%)", "hsl(0 0% 0%)", "hsl(0 0% 15%)", "hsl(0 0% 100%)", "hsl(0 0% 12%)", "hsl(0 0% 40%)"),
  },
  {
    id: "mono", label: "Mono",
    light: v("hsl(0 0% 97%)", "hsl(0 0% 3%)", "hsl(0 0% 100%)", "hsl(0 0% 3%)", "hsl(0 0% 6%)", "hsl(0 0% 97%)", "hsl(0 0% 90%)", "hsl(0 0% 6%)", "hsl(0 0% 93%)", "hsl(0 0% 38%)", "hsl(0 0% 87%)", "hsl(0 0% 6%)", "hsl(0 72% 51%)", "hsl(0 0% 84%)", "hsl(0 0% 56%)", "hsl(0 0% 3%)", "0rem", "hsl(0 0% 97%)", "hsl(0 0% 3%)", "hsl(0 0% 6%)", "hsl(0 0% 97%)", "hsl(0 0% 87%)", "hsl(0 0% 6%)", "hsl(0 0% 84%)", "hsl(0 0% 3%)"),
    dark: v("hsl(0 0% 5%)", "hsl(0 0% 96%)", "hsl(0 0% 8%)", "hsl(0 0% 96%)", "hsl(0 0% 96%)", "hsl(0 0% 5%)", "hsl(0 0% 14%)", "hsl(0 0% 96%)", "hsl(0 0% 12%)", "hsl(0 0% 53%)", "hsl(0 0% 16%)", "hsl(0 0% 96%)", "hsl(0 72% 51%)", "hsl(0 0% 15%)", "hsl(0 0% 39%)", "hsl(0 0% 39%)", "0rem", "hsl(0 0% 6%)", "hsl(0 0% 96%)", "hsl(0 0% 96%)", "hsl(0 0% 5%)", "hsl(0 0% 16%)", "hsl(0 0% 96%)", "hsl(0 0% 13%)", "hsl(0 0% 30%)"),
  },
  {
    id: "graphite", label: "Graphite",
    light: v("hsl(210 17% 97%)", "hsl(215 28% 17%)", "hsl(0 0% 100%)", "hsl(215 28% 17%)", "hsl(215 28% 17%)", "hsl(0 0% 100%)", "hsl(210 14% 89%)", "hsl(215 28% 17%)", "hsl(210 14% 92%)", "hsl(215 13% 42%)", "hsl(210 14% 89%)", "hsl(215 28% 17%)", "hsl(0 72% 51%)", "hsl(210 14% 83%)", "hsl(210 14% 57%)", "hsl(215 28% 17%)", "0.5rem", "hsl(210 17% 95%)", "hsl(215 28% 17%)", "hsl(215 28% 17%)", "hsl(0 0% 100%)", "hsl(210 14% 89%)", "hsl(215 28% 17%)", "hsl(210 14% 83%)", "hsl(215 28% 17%)"),
    dark: v("hsl(215 28% 7%)", "hsl(210 17% 88%)", "hsl(215 25% 10%)", "hsl(210 17% 88%)", "hsl(210 17% 88%)", "hsl(215 28% 7%)", "hsl(215 22% 15%)", "hsl(210 17% 88%)", "hsl(215 22% 12%)", "hsl(215 13% 54%)", "hsl(215 22% 18%)", "hsl(210 17% 88%)", "hsl(0 72% 51%)", "hsl(215 22% 17%)", "hsl(215 22% 42%)", "hsl(215 13% 41%)", "0.5rem", "hsl(215 28% 8%)", "hsl(210 17% 88%)", "hsl(210 17% 88%)", "hsl(215 28% 7%)", "hsl(215 22% 15%)", "hsl(210 17% 88%)", "hsl(215 22% 14%)", "hsl(215 13% 38%)"),
  },

  // ── App-Themes ─────────────────────────────────────────────────────────────
  {
    id: "claude", label: "Claude",
    light: v("hsl(45 33% 97%)", "hsl(35 18% 20%)", "hsl(45 33% 98%)", "hsl(35 18% 20%)", "hsl(18 51% 41%)", "hsl(0 0% 100%)", "hsl(40 22% 87%)", "hsl(35 18% 20%)", "hsl(40 22% 90%)", "hsl(35 12% 39%)", "hsl(40 22% 87%)", "hsl(35 18% 20%)", "hsl(0 72% 51%)", "hsl(40 20% 85%)", "hsl(40 20% 52%)", "hsl(18 51% 52%)", "0.5rem", "hsl(45 33% 95%)", "hsl(35 18% 20%)", "hsl(18 51% 46%)", "hsl(0 0% 100%)", "hsl(40 22% 87%)", "hsl(35 18% 20%)", "hsl(40 20% 83%)", "hsl(18 51% 52%)"),
    dark: v("hsl(40 9% 15%)", "hsl(40 13% 76%)", "hsl(40 9% 17%)", "hsl(40 13% 76%)", "hsl(18 57% 46%)", "hsl(0 0% 100%)", "hsl(40 9% 10%)", "hsl(40 13% 76%)", "hsl(40 9% 21%)", "hsl(40 10% 62%)", "hsl(40 9% 12%)", "hsl(40 13% 76%)", "hsl(0 72% 51%)", "hsl(40 9% 21%)", "hsl(40 9% 45%)", "hsl(18 57% 59%)", "0.5rem", "hsl(40 9% 12%)", "hsl(40 13% 76%)", "hsl(18 57% 46%)", "hsl(0 0% 100%)", "hsl(40 9% 18%)", "hsl(40 13% 76%)", "hsl(40 9% 19%)", "hsl(18 57% 59%)"),
  },
  {
    // Ohne "Outfit": die Schrift kam von fonts.googleapis.com (s. `Theme.fontSans`).
    // Das Theme behaelt seine Farben und laeuft wie die uebrigen 43 in Geist.
    id: "supabase", label: "Supabase",
    light: v("hsl(0 0% 99%)", "hsl(0 0% 9%)", "hsl(0 0% 99%)", "hsl(0 0% 9%)", "hsl(153 60% 40%)", "hsl(0 0% 9%)", "hsl(0 0% 99%)", "hsl(0 0% 9%)", "hsl(0 0% 93%)", "hsl(0 0% 42%)", "hsl(0 0% 93%)", "hsl(0 0% 9%)", "hsl(0 72% 51%)", "hsl(0 0% 89%)", "hsl(0 0% 57%)", "hsl(153 60% 40%)", "0.5rem", "hsl(0 0% 97%)", "hsl(0 0% 9%)", "hsl(153 60% 39%)", "hsl(0 0% 9%)", "hsl(0 0% 93%)", "hsl(0 0% 9%)", "hsl(0 0% 89%)", "hsl(153 60% 53%)"),
    dark: v("hsl(0 0% 7%)", "hsl(214 32% 91%)", "hsl(0 0% 9%)", "hsl(214 32% 91%)", "hsl(153 100% 23%)", "hsl(214 32% 91%)", "hsl(0 0% 12%)", "hsl(214 32% 91%)", "hsl(0 0% 12%)", "hsl(0 0% 55%)", "hsl(0 0% 19%)", "hsl(214 32% 91%)", "hsl(0 72% 51%)", "hsl(0 0% 14%)", "hsl(0 0% 40%)", "hsl(153 60% 53%)", "0.5rem", "hsl(0 0% 5%)", "hsl(214 32% 91%)", "hsl(153 100% 22%)", "hsl(214 32% 91%)", "hsl(0 0% 12%)", "hsl(214 32% 91%)", "hsl(0 0% 12%)", "hsl(153 60% 53%)"),
  },
  {
    id: "t3-chat", label: "T3 Chat",
    light: v("hsl(300 33% 97%)", "hsl(297 59% 20%)", "hsl(300 33% 97%)", "hsl(297 59% 20%)", "hsl(334 57% 47%)", "hsl(0 0% 100%)", "hsl(302 56% 87%)", "hsl(297 59% 20%)", "hsl(306 47% 93%)", "hsl(297 40% 40%)", "hsl(302 56% 87%)", "hsl(297 59% 20%)", "hsl(0 72% 51%)", "hsl(302 47% 87%)", "hsl(302 47% 60%)", "hsl(328 78% 42%)", "0.5rem", "hsl(300 33% 95%)", "hsl(297 59% 20%)", "hsl(334 57% 47%)", "hsl(0 0% 100%)", "hsl(302 56% 87%)", "hsl(297 59% 20%)", "hsl(302 47% 87%)", "hsl(328 78% 42%)"),
    dark: v("hsl(267 15% 14%)", "hsl(290 34% 88%)", "hsl(267 15% 16%)", "hsl(290 34% 88%)", "hsl(334 100% 43%)", "hsl(290 34% 96%)", "hsl(266 18% 27%)", "hsl(290 34% 88%)", "hsl(267 15% 18%)", "hsl(290 20% 65%)", "hsl(266 18% 27%)", "hsl(290 34% 88%)", "hsl(0 72% 51%)", "hsl(267 15% 22%)", "hsl(267 15% 49%)", "hsl(334 100% 43%)", "0.5rem", "hsl(267 15% 12%)", "hsl(290 34% 88%)", "hsl(334 100% 40%)", "hsl(290 34% 92%)", "hsl(266 18% 27%)", "hsl(290 34% 88%)", "hsl(267 15% 20%)", "hsl(334 100% 30%)"),
  },

  // ── Blue / Indigo ──────────────────────────────────────────────────────────
  {
    id: "blue", label: "Blue",
    light: v("hsl(210 40% 98%)", "hsl(222 47% 11%)", "hsl(0 0% 100%)", "hsl(222 47% 11%)", "hsl(221 83% 52%)", "hsl(0 0% 100%)", "hsl(214 32% 91%)", "hsl(222 47% 11%)", "hsl(214 32% 91%)", "hsl(215 16% 42%)", "hsl(214 32% 91%)", "hsl(222 47% 11%)", "hsl(0 72% 51%)", "hsl(214 32% 86%)", "hsl(214 32% 59%)", "hsl(221 83% 53%)", "0.5rem", "hsl(210 40% 96%)", "hsl(222 47% 11%)", "hsl(221 83% 53%)", "hsl(0 0% 100%)", "hsl(214 32% 91%)", "hsl(222 47% 11%)", "hsl(214 32% 83%)", "hsl(221 83% 53%)"),
    dark: v("hsl(222 47% 5%)", "hsl(213 31% 91%)", "hsl(222 47% 8%)", "hsl(213 31% 91%)", "hsl(217 91% 53%)", "hsl(0 0% 100%)", "hsl(217 33% 17%)", "hsl(213 31% 91%)", "hsl(217 33% 13%)", "hsl(215 20% 55%)", "hsl(217 33% 17%)", "hsl(213 31% 91%)", "hsl(0 72% 51%)", "hsl(217 33% 18%)", "hsl(217 33% 42%)", "hsl(217 91% 60%)", "0.5rem", "hsl(222 47% 6%)", "hsl(213 31% 91%)", "hsl(217 91% 53%)", "hsl(0 0% 100%)", "hsl(217 33% 17%)", "hsl(213 31% 91%)", "hsl(217 33% 15%)", "hsl(217 91% 60%)"),
  },
  {
    id: "indigo", label: "Indigo",
    light: v("hsl(240 20% 98%)", "hsl(243 75% 20%)", "hsl(0 0% 100%)", "hsl(243 75% 20%)", "hsl(239 84% 63%)", "hsl(0 0% 100%)", "hsl(240 33% 90%)", "hsl(243 75% 20%)", "hsl(240 33% 93%)", "hsl(240 15% 46%)", "hsl(240 33% 90%)", "hsl(243 75% 20%)", "hsl(0 72% 51%)", "hsl(240 26% 83%)", "hsl(240 33% 66%)", "hsl(239 84% 67%)", "0.5rem", "hsl(240 20% 96%)", "hsl(243 75% 20%)", "hsl(239 84% 66%)", "hsl(0 0% 100%)", "hsl(240 33% 90%)", "hsl(243 75% 20%)", "hsl(240 26% 83%)", "hsl(239 84% 67%)"),
    dark: v("hsl(240 30% 6%)", "hsl(240 30% 90%)", "hsl(240 30% 9%)", "hsl(240 30% 90%)", "hsl(239 84% 66%)", "hsl(0 0% 100%)", "hsl(240 25% 17%)", "hsl(240 30% 90%)", "hsl(240 25% 13%)", "hsl(240 15% 57%)", "hsl(240 25% 17%)", "hsl(240 30% 90%)", "hsl(0 72% 51%)", "hsl(240 25% 18%)", "hsl(240 25% 48%)", "hsl(239 84% 67%)", "0.5rem", "hsl(240 30% 7%)", "hsl(240 30% 90%)", "hsl(239 84% 66%)", "hsl(0 0% 100%)", "hsl(240 25% 17%)", "hsl(240 30% 90%)", "hsl(240 25% 15%)", "hsl(239 84% 67%)"),
  },
  {
    id: "sky", label: "Sky",
    light: v("hsl(204 45% 98%)", "hsl(202 80% 16%)", "hsl(0 0% 100%)", "hsl(202 80% 16%)", "hsl(199 89% 33%)", "hsl(0 0% 100%)", "hsl(201 35% 88%)", "hsl(202 80% 16%)", "hsl(201 35% 92%)", "hsl(200 18% 40%)", "hsl(201 35% 88%)", "hsl(202 80% 16%)", "hsl(0 72% 51%)", "hsl(201 35% 82%)", "hsl(201 35% 54%)", "hsl(199 89% 45%)", "0.5rem", "hsl(204 45% 96%)", "hsl(202 80% 16%)", "hsl(199 89% 37%)", "hsl(0 0% 100%)", "hsl(201 35% 88%)", "hsl(202 80% 16%)", "hsl(201 35% 82%)", "hsl(199 89% 48%)"),
    dark: v("hsl(202 80% 5%)", "hsl(201 35% 89%)", "hsl(202 70% 8%)", "hsl(201 35% 89%)", "hsl(199 89% 37%)", "hsl(0 0% 100%)", "hsl(201 40% 16%)", "hsl(201 35% 89%)", "hsl(201 40% 12%)", "hsl(200 18% 56%)", "hsl(201 40% 16%)", "hsl(201 35% 89%)", "hsl(0 72% 51%)", "hsl(201 40% 17%)", "hsl(201 40% 38%)", "hsl(199 89% 48%)", "0.5rem", "hsl(202 70% 6%)", "hsl(201 35% 89%)", "hsl(199 89% 37%)", "hsl(0 0% 100%)", "hsl(201 40% 16%)", "hsl(201 35% 89%)", "hsl(201 40% 14%)", "hsl(199 89% 48%)"),
  },

  // ── Teal / Cyan / Emerald ─────────────────────────────────────────────────
  {
    id: "teal", label: "Teal",
    light: v("hsl(180 30% 98%)", "hsl(180 75% 12%)", "hsl(0 0% 100%)", "hsl(180 75% 12%)", "hsl(173 80% 25%)", "hsl(0 0% 100%)", "hsl(174 30% 88%)", "hsl(180 75% 12%)", "hsl(174 30% 92%)", "hsl(175 18% 38%)", "hsl(174 30% 88%)", "hsl(180 75% 12%)", "hsl(0 72% 51%)", "hsl(174 30% 82%)", "hsl(174 30% 47%)", "hsl(173 80% 35%)", "0.5rem", "hsl(180 30% 96%)", "hsl(180 75% 12%)", "hsl(173 80% 29%)", "hsl(0 0% 100%)", "hsl(174 30% 88%)", "hsl(180 75% 12%)", "hsl(174 30% 82%)", "hsl(173 80% 40%)"),
    dark: v("hsl(180 75% 4%)", "hsl(174 30% 89%)", "hsl(180 65% 7%)", "hsl(174 30% 89%)", "hsl(173 80% 29%)", "hsl(0 0% 100%)", "hsl(174 35% 14%)", "hsl(174 30% 89%)", "hsl(174 35% 11%)", "hsl(175 18% 54%)", "hsl(174 35% 14%)", "hsl(174 30% 89%)", "hsl(0 72% 51%)", "hsl(174 35% 16%)", "hsl(174 35% 33%)", "hsl(173 80% 40%)", "0.5rem", "hsl(180 65% 5%)", "hsl(174 30% 89%)", "hsl(173 80% 29%)", "hsl(0 0% 100%)", "hsl(174 35% 14%)", "hsl(174 30% 89%)", "hsl(174 35% 12%)", "hsl(173 80% 40%)"),
  },
  {
    id: "cyan", label: "Cyan",
    light: v("hsl(190 40% 98%)", "hsl(192 88% 13%)", "hsl(0 0% 100%)", "hsl(192 88% 13%)", "hsl(189 94% 27%)", "hsl(0 0% 100%)", "hsl(190 35% 88%)", "hsl(192 88% 13%)", "hsl(190 35% 92%)", "hsl(191 18% 39%)", "hsl(190 35% 88%)", "hsl(192 88% 13%)", "hsl(0 72% 51%)", "hsl(190 35% 82%)", "hsl(190 35% 49%)", "hsl(189 94% 37%)", "0.5rem", "hsl(190 40% 96%)", "hsl(192 88% 13%)", "hsl(189 94% 30%)", "hsl(0 0% 100%)", "hsl(190 35% 88%)", "hsl(192 88% 13%)", "hsl(190 35% 82%)", "hsl(189 94% 43%)"),
    dark: v("hsl(192 88% 4%)", "hsl(190 35% 89%)", "hsl(192 78% 7%)", "hsl(190 35% 89%)", "hsl(189 94% 30%)", "hsl(0 0% 100%)", "hsl(190 40% 14%)", "hsl(190 35% 89%)", "hsl(190 40% 11%)", "hsl(191 18% 54%)", "hsl(190 40% 14%)", "hsl(190 35% 89%)", "hsl(0 72% 51%)", "hsl(190 40% 16%)", "hsl(190 40% 34%)", "hsl(189 94% 43%)", "0.5rem", "hsl(192 78% 5%)", "hsl(190 35% 89%)", "hsl(189 94% 30%)", "hsl(0 0% 100%)", "hsl(190 40% 14%)", "hsl(190 35% 89%)", "hsl(190 40% 12%)", "hsl(189 94% 43%)"),
  },
  {
    id: "emerald", label: "Emerald",
    light: v("hsl(152 40% 98%)", "hsl(152 80% 11%)", "hsl(0 0% 100%)", "hsl(152 80% 11%)", "hsl(152 69% 28%)", "hsl(0 0% 100%)", "hsl(152 32% 88%)", "hsl(152 80% 11%)", "hsl(152 32% 92%)", "hsl(152 15% 39%)", "hsl(152 32% 88%)", "hsl(152 80% 11%)", "hsl(0 72% 51%)", "hsl(152 32% 82%)", "hsl(152 32% 47%)", "hsl(152 69% 31%)", "0.5rem", "hsl(152 40% 96%)", "hsl(152 80% 11%)", "hsl(152 69% 31%)", "hsl(0 0% 100%)", "hsl(152 32% 88%)", "hsl(152 80% 11%)", "hsl(152 32% 82%)", "hsl(152 69% 31%)"),
    dark: v("hsl(152 80% 4%)", "hsl(152 32% 89%)", "hsl(152 70% 7%)", "hsl(152 32% 89%)", "hsl(152 69% 31%)", "hsl(0 0% 100%)", "hsl(152 35% 14%)", "hsl(152 32% 89%)", "hsl(152 35% 11%)", "hsl(152 15% 54%)", "hsl(152 35% 14%)", "hsl(152 32% 89%)", "hsl(0 72% 51%)", "hsl(152 35% 16%)", "hsl(152 35% 34%)", "hsl(152 69% 31%)", "0.5rem", "hsl(152 70% 5%)", "hsl(152 32% 89%)", "hsl(152 69% 31%)", "hsl(0 0% 100%)", "hsl(152 35% 14%)", "hsl(152 32% 89%)", "hsl(152 35% 12%)", "hsl(152 69% 31%)"),
  },
  {
    id: "green", label: "Green",
    light: v("hsl(138 35% 97%)", "hsl(140 70% 12%)", "hsl(0 0% 100%)", "hsl(140 70% 12%)", "hsl(142 71% 27%)", "hsl(0 0% 100%)", "hsl(141 29% 89%)", "hsl(140 70% 12%)", "hsl(141 29% 92%)", "hsl(140 15% 39%)", "hsl(141 29% 89%)", "hsl(140 70% 12%)", "hsl(0 72% 51%)", "hsl(141 29% 83%)", "hsl(141 29% 48%)", "hsl(142 71% 38%)", "0.5rem", "hsl(138 35% 95%)", "hsl(140 70% 12%)", "hsl(142 71% 31%)", "hsl(0 0% 100%)", "hsl(141 29% 89%)", "hsl(140 70% 12%)", "hsl(141 29% 83%)", "hsl(142 71% 45%)"),
    dark: v("hsl(140 70% 4%)", "hsl(141 29% 89%)", "hsl(140 60% 7%)", "hsl(141 29% 89%)", "hsl(142 71% 31%)", "hsl(0 0% 100%)", "hsl(141 34% 14%)", "hsl(141 29% 89%)", "hsl(141 34% 11%)", "hsl(140 15% 54%)", "hsl(141 34% 14%)", "hsl(141 29% 89%)", "hsl(0 72% 51%)", "hsl(141 34% 16%)", "hsl(141 34% 34%)", "hsl(142 71% 45%)", "0.5rem", "hsl(140 60% 5%)", "hsl(141 29% 89%)", "hsl(142 71% 31%)", "hsl(0 0% 100%)", "hsl(141 34% 14%)", "hsl(141 29% 89%)", "hsl(141 34% 12%)", "hsl(142 71% 45%)"),
  },

  // ── Yellow / Amber / Orange ────────────────────────────────────────────────
  {
    id: "amber-minimal", label: "Amber",
    light: v("hsl(45 100% 97%)", "hsl(35 92% 15%)", "hsl(0 0% 100%)", "hsl(35 92% 15%)", "hsl(38 92% 41%)", "hsl(35 92% 12%)", "hsl(43 67% 88%)", "hsl(35 92% 15%)", "hsl(43 67% 92%)", "hsl(38 30% 38%)", "hsl(43 67% 88%)", "hsl(35 92% 15%)", "hsl(0 72% 51%)", "hsl(43 50% 82%)", "hsl(43 67% 42%)", "hsl(38 92% 41%)", "0.5rem", "hsl(45 100% 95%)", "hsl(35 92% 15%)", "hsl(38 92% 40%)", "hsl(35 92% 11%)", "hsl(43 67% 88%)", "hsl(35 92% 15%)", "hsl(43 50% 82%)", "hsl(38 92% 50%)"),
    dark: v("hsl(35 30% 9%)", "hsl(43 67% 88%)", "hsl(35 28% 12%)", "hsl(43 67% 88%)", "hsl(38 92% 50%)", "hsl(35 92% 12%)", "hsl(35 20% 18%)", "hsl(43 67% 88%)", "hsl(35 20% 14%)", "hsl(38 20% 58%)", "hsl(35 20% 18%)", "hsl(43 67% 88%)", "hsl(0 72% 51%)", "hsl(35 20% 20%)", "hsl(35 20% 41%)", "hsl(38 92% 50%)", "0.5rem", "hsl(35 28% 10%)", "hsl(43 67% 88%)", "hsl(38 92% 50%)", "hsl(35 92% 12%)", "hsl(35 20% 18%)", "hsl(43 67% 88%)", "hsl(35 20% 16%)", "hsl(38 92% 50%)"),
  },
  {
    id: "orange", label: "Orange",
    light: v("hsl(30 100% 98%)", "hsl(20 85% 18%)", "hsl(0 0% 100%)", "hsl(20 85% 18%)", "hsl(25 95% 35%)", "hsl(0 0% 100%)", "hsl(28 60% 88%)", "hsl(20 85% 18%)", "hsl(28 60% 92%)", "hsl(24 18% 41%)", "hsl(28 60% 88%)", "hsl(20 85% 18%)", "hsl(0 72% 51%)", "hsl(28 50% 82%)", "hsl(28 60% 51%)", "hsl(25 95% 48%)", "0.5rem", "hsl(30 100% 96%)", "hsl(20 85% 18%)", "hsl(25 95% 39%)", "hsl(0 0% 100%)", "hsl(28 60% 88%)", "hsl(20 85% 18%)", "hsl(28 50% 82%)", "hsl(25 95% 53%)"),
    dark: v("hsl(20 85% 6%)", "hsl(28 60% 89%)", "hsl(20 75% 9%)", "hsl(28 60% 89%)", "hsl(25 95% 39%)", "hsl(0 0% 100%)", "hsl(22 30% 16%)", "hsl(28 60% 89%)", "hsl(22 30% 12%)", "hsl(24 18% 55%)", "hsl(22 30% 16%)", "hsl(28 60% 89%)", "hsl(0 72% 51%)", "hsl(22 30% 18%)", "hsl(22 30% 40%)", "hsl(25 95% 53%)", "0.5rem", "hsl(20 75% 7%)", "hsl(28 60% 89%)", "hsl(25 95% 39%)", "hsl(0 0% 100%)", "hsl(22 30% 16%)", "hsl(28 60% 89%)", "hsl(22 30% 14%)", "hsl(25 95% 53%)"),
  },
  {
    id: "yellow", label: "Yellow",
    light: v("hsl(55 100% 97%)", "hsl(40 90% 14%)", "hsl(0 0% 100%)", "hsl(40 90% 14%)", "hsl(48 96% 35%)", "hsl(40 90% 11%)", "hsl(52 80% 88%)", "hsl(40 90% 14%)", "hsl(52 80% 92%)", "hsl(46 22% 39%)", "hsl(52 80% 88%)", "hsl(40 90% 14%)", "hsl(0 72% 51%)", "hsl(52 60% 82%)", "hsl(52 80% 36%)", "hsl(48 96% 35%)", "0.5rem", "hsl(55 100% 95%)", "hsl(40 90% 14%)", "hsl(48 96% 35%)", "hsl(40 90% 11%)", "hsl(52 80% 88%)", "hsl(40 90% 14%)", "hsl(52 60% 82%)", "hsl(48 96% 53%)"),
    dark: v("hsl(40 90% 5%)", "hsl(52 80% 88%)", "hsl(40 80% 8%)", "hsl(52 80% 88%)", "hsl(48 96% 53%)", "hsl(40 90% 14%)", "hsl(44 30% 16%)", "hsl(52 80% 88%)", "hsl(44 30% 12%)", "hsl(46 20% 56%)", "hsl(44 30% 16%)", "hsl(52 80% 88%)", "hsl(0 72% 51%)", "hsl(44 30% 18%)", "hsl(44 30% 36%)", "hsl(48 96% 53%)", "0.5rem", "hsl(40 80% 6%)", "hsl(52 80% 88%)", "hsl(48 96% 53%)", "hsl(40 90% 14%)", "hsl(44 30% 16%)", "hsl(52 80% 88%)", "hsl(44 30% 14%)", "hsl(48 96% 53%)"),
  },

  // ── Red / Rose / Pink ─────────────────────────────────────────────────────
  {
    id: "red", label: "Red",
    light: v("hsl(0 33% 98%)", "hsl(0 80% 16%)", "hsl(0 0% 100%)", "hsl(0 80% 16%)", "hsl(0 72% 49%)", "hsl(0 0% 100%)", "hsl(0 30% 89%)", "hsl(0 80% 16%)", "hsl(0 30% 92%)", "hsl(0 15% 44%)", "hsl(0 30% 89%)", "hsl(0 80% 16%)", "hsl(0 72% 51%)", "hsl(0 30% 83%)", "hsl(0 30% 62%)", "hsl(0 72% 51%)", "0.5rem", "hsl(0 33% 96%)", "hsl(0 80% 16%)", "hsl(0 72% 51%)", "hsl(0 0% 100%)", "hsl(0 30% 89%)", "hsl(0 80% 16%)", "hsl(0 30% 83%)", "hsl(0 72% 51%)"),
    dark: v("hsl(0 80% 5%)", "hsl(0 30% 89%)", "hsl(0 70% 8%)", "hsl(0 30% 89%)", "hsl(0 72% 51%)", "hsl(0 0% 100%)", "hsl(0 35% 14%)", "hsl(0 30% 89%)", "hsl(0 35% 11%)", "hsl(0 15% 54%)", "hsl(0 35% 14%)", "hsl(0 30% 89%)", "hsl(0 72% 51%)", "hsl(0 35% 16%)", "hsl(0 35% 44%)", "hsl(0 72% 51%)", "0.5rem", "hsl(0 70% 6%)", "hsl(0 30% 89%)", "hsl(0 72% 51%)", "hsl(0 0% 100%)", "hsl(0 35% 14%)", "hsl(0 30% 89%)", "hsl(0 35% 12%)", "hsl(0 72% 51%)"),
  },
  {
    id: "rose", label: "Rose",
    light: v("hsl(350 33% 98%)", "hsl(346 77% 16%)", "hsl(0 0% 100%)", "hsl(346 77% 16%)", "hsl(346 77% 48%)", "hsl(0 0% 100%)", "hsl(348 30% 89%)", "hsl(346 77% 16%)", "hsl(348 30% 92%)", "hsl(346 14% 43%)", "hsl(348 30% 89%)", "hsl(346 77% 16%)", "hsl(0 72% 51%)", "hsl(348 30% 83%)", "hsl(348 30% 62%)", "hsl(346 77% 49%)", "0.5rem", "hsl(350 33% 96%)", "hsl(346 77% 16%)", "hsl(346 77% 49%)", "hsl(0 0% 100%)", "hsl(348 30% 89%)", "hsl(346 77% 16%)", "hsl(348 30% 83%)", "hsl(346 77% 49%)"),
    dark: v("hsl(346 77% 5%)", "hsl(348 30% 89%)", "hsl(346 67% 8%)", "hsl(348 30% 89%)", "hsl(346 77% 49%)", "hsl(0 0% 100%)", "hsl(347 35% 14%)", "hsl(348 30% 89%)", "hsl(347 35% 11%)", "hsl(346 14% 54%)", "hsl(347 35% 14%)", "hsl(348 30% 89%)", "hsl(0 72% 51%)", "hsl(347 35% 16%)", "hsl(347 35% 44%)", "hsl(346 77% 49%)", "0.5rem", "hsl(346 67% 6%)", "hsl(348 30% 89%)", "hsl(346 77% 49%)", "hsl(0 0% 100%)", "hsl(347 35% 14%)", "hsl(348 30% 89%)", "hsl(347 35% 12%)", "hsl(346 77% 49%)"),
  },
  {
    id: "pink", label: "Pink",
    light: v("hsl(330 40% 98%)", "hsl(328 72% 17%)", "hsl(0 0% 100%)", "hsl(328 72% 17%)", "hsl(330 81% 46%)", "hsl(0 0% 100%)", "hsl(328 35% 89%)", "hsl(328 72% 17%)", "hsl(328 35% 92%)", "hsl(328 16% 43%)", "hsl(328 35% 89%)", "hsl(328 72% 17%)", "hsl(0 72% 51%)", "hsl(328 30% 83%)", "hsl(328 35% 61%)", "hsl(330 81% 60%)", "0.5rem", "hsl(330 40% 96%)", "hsl(328 72% 17%)", "hsl(330 81% 49%)", "hsl(0 0% 100%)", "hsl(328 35% 89%)", "hsl(328 72% 17%)", "hsl(328 30% 83%)", "hsl(330 81% 60%)"),
    dark: v("hsl(328 72% 5%)", "hsl(328 35% 89%)", "hsl(328 62% 8%)", "hsl(328 35% 89%)", "hsl(330 81% 49%)", "hsl(0 0% 100%)", "hsl(328 35% 14%)", "hsl(328 35% 89%)", "hsl(328 35% 11%)", "hsl(328 16% 54%)", "hsl(328 35% 14%)", "hsl(328 35% 89%)", "hsl(0 72% 51%)", "hsl(328 35% 16%)", "hsl(328 35% 43%)", "hsl(330 81% 60%)", "0.5rem", "hsl(328 62% 6%)", "hsl(328 35% 89%)", "hsl(330 81% 49%)", "hsl(0 0% 100%)", "hsl(328 35% 14%)", "hsl(328 35% 89%)", "hsl(328 35% 12%)", "hsl(330 81% 60%)"),
  },
  {
    id: "fuchsia", label: "Fuchsia",
    light: v("hsl(292 40% 98%)", "hsl(295 72% 17%)", "hsl(0 0% 100%)", "hsl(295 72% 17%)", "hsl(292 84% 45%)", "hsl(0 0% 100%)", "hsl(294 36% 89%)", "hsl(295 72% 17%)", "hsl(294 36% 92%)", "hsl(294 16% 43%)", "hsl(294 36% 89%)", "hsl(295 72% 17%)", "hsl(0 72% 51%)", "hsl(294 30% 83%)", "hsl(294 36% 61%)", "hsl(292 84% 61%)", "0.5rem", "hsl(292 40% 96%)", "hsl(295 72% 17%)", "hsl(292 84% 48%)", "hsl(0 0% 100%)", "hsl(294 36% 89%)", "hsl(295 72% 17%)", "hsl(294 30% 83%)", "hsl(292 84% 61%)"),
    dark: v("hsl(295 72% 5%)", "hsl(294 36% 89%)", "hsl(295 62% 8%)", "hsl(294 36% 89%)", "hsl(292 84% 48%)", "hsl(0 0% 100%)", "hsl(294 35% 14%)", "hsl(294 36% 89%)", "hsl(294 35% 11%)", "hsl(294 16% 54%)", "hsl(294 35% 14%)", "hsl(294 36% 89%)", "hsl(0 72% 51%)", "hsl(294 35% 16%)", "hsl(294 35% 43%)", "hsl(292 84% 61%)", "0.5rem", "hsl(295 62% 6%)", "hsl(294 36% 89%)", "hsl(292 84% 48%)", "hsl(0 0% 100%)", "hsl(294 35% 14%)", "hsl(294 36% 89%)", "hsl(294 35% 12%)", "hsl(292 84% 61%)"),
  },

  // ── Purple / Violet ───────────────────────────────────────────────────────
  {
    id: "violet", label: "Violet",
    light: v("hsl(270 35% 98%)", "hsl(272 72% 17%)", "hsl(0 0% 100%)", "hsl(272 72% 17%)", "hsl(262 83% 58%)", "hsl(0 0% 100%)", "hsl(270 36% 89%)", "hsl(272 72% 17%)", "hsl(270 36% 92%)", "hsl(270 16% 44%)", "hsl(270 36% 89%)", "hsl(272 72% 17%)", "hsl(0 72% 51%)", "hsl(270 30% 83%)", "hsl(270 36% 64%)", "hsl(262 83% 58%)", "0.5rem", "hsl(270 35% 96%)", "hsl(272 72% 17%)", "hsl(262 83% 58%)", "hsl(0 0% 100%)", "hsl(270 36% 89%)", "hsl(272 72% 17%)", "hsl(270 30% 83%)", "hsl(262 83% 58%)"),
    dark: v("hsl(272 72% 5%)", "hsl(270 36% 89%)", "hsl(272 62% 8%)", "hsl(270 36% 89%)", "hsl(262 83% 58%)", "hsl(0 0% 100%)", "hsl(270 35% 14%)", "hsl(270 36% 89%)", "hsl(270 35% 11%)", "hsl(270 16% 55%)", "hsl(270 35% 14%)", "hsl(270 36% 89%)", "hsl(0 72% 51%)", "hsl(270 35% 16%)", "hsl(270 35% 47%)", "hsl(262 83% 58%)", "0.5rem", "hsl(272 62% 6%)", "hsl(270 36% 89%)", "hsl(262 83% 58%)", "hsl(0 0% 100%)", "hsl(270 35% 14%)", "hsl(270 36% 89%)", "hsl(270 35% 12%)", "hsl(262 83% 58%)"),
  },
  {
    id: "purple", label: "Purple",
    light: v("hsl(280 35% 98%)", "hsl(285 72% 15%)", "hsl(0 0% 100%)", "hsl(285 72% 15%)", "hsl(280 68% 52%)", "hsl(0 0% 100%)", "hsl(280 33% 89%)", "hsl(285 72% 15%)", "hsl(280 33% 92%)", "hsl(280 15% 44%)", "hsl(280 33% 89%)", "hsl(285 72% 15%)", "hsl(0 72% 51%)", "hsl(280 28% 83%)", "hsl(280 33% 62%)", "hsl(280 68% 60%)", "0.5rem", "hsl(280 35% 96%)", "hsl(285 72% 15%)", "hsl(280 68% 57%)", "hsl(0 0% 100%)", "hsl(280 33% 89%)", "hsl(285 72% 15%)", "hsl(280 28% 83%)", "hsl(280 68% 60%)"),
    dark: v("hsl(285 72% 5%)", "hsl(280 33% 89%)", "hsl(285 62% 8%)", "hsl(280 33% 89%)", "hsl(280 68% 57%)", "hsl(0 0% 100%)", "hsl(280 33% 14%)", "hsl(280 33% 89%)", "hsl(280 33% 11%)", "hsl(280 15% 54%)", "hsl(280 33% 14%)", "hsl(280 33% 89%)", "hsl(0 72% 51%)", "hsl(280 33% 16%)", "hsl(280 33% 45%)", "hsl(280 68% 60%)", "0.5rem", "hsl(285 62% 6%)", "hsl(280 33% 89%)", "hsl(280 68% 57%)", "hsl(0 0% 100%)", "hsl(280 33% 14%)", "hsl(280 33% 89%)", "hsl(280 33% 12%)", "hsl(280 68% 60%)"),
  },

  // ── Lime ──────────────────────────────────────────────────────────────────
  {
    id: "lime", label: "Lime",
    light: v("hsl(80 50% 97%)", "hsl(82 72% 14%)", "hsl(0 0% 100%)", "hsl(82 72% 14%)", "hsl(84 85% 34%)", "hsl(82 72% 10%)", "hsl(82 40% 88%)", "hsl(82 72% 14%)", "hsl(82 40% 92%)", "hsl(82 18% 38%)", "hsl(82 40% 88%)", "hsl(82 72% 14%)", "hsl(0 72% 51%)", "hsl(82 35% 82%)", "hsl(82 40% 43%)", "hsl(84 85% 34%)", "0.5rem", "hsl(80 50% 95%)", "hsl(82 72% 14%)", "hsl(84 85% 33%)", "hsl(82 72% 9%)", "hsl(82 40% 88%)", "hsl(82 72% 14%)", "hsl(82 35% 82%)", "hsl(84 85% 44%)"),
    dark: v("hsl(82 72% 5%)", "hsl(82 40% 88%)", "hsl(82 62% 8%)", "hsl(82 40% 88%)", "hsl(84 85% 44%)", "hsl(82 72% 10%)", "hsl(82 33% 14%)", "hsl(82 40% 88%)", "hsl(82 33% 11%)", "hsl(82 18% 54%)", "hsl(82 33% 14%)", "hsl(82 40% 88%)", "hsl(0 72% 51%)", "hsl(82 33% 16%)", "hsl(82 33% 34%)", "hsl(84 85% 44%)", "0.5rem", "hsl(82 62% 6%)", "hsl(82 40% 88%)", "hsl(84 85% 44%)", "hsl(82 72% 10%)", "hsl(82 33% 14%)", "hsl(82 40% 88%)", "hsl(82 33% 12%)", "hsl(84 85% 44%)"),
  },

  // ── Warm / Earth ──────────────────────────────────────────────────────────
  {
    id: "mocha-mousse", label: "Mocha",
    light: v("hsl(30 25% 97%)", "hsl(25 35% 18%)", "hsl(0 0% 100%)", "hsl(25 35% 18%)", "hsl(20 40% 41%)", "hsl(0 0% 100%)", "hsl(28 22% 89%)", "hsl(25 35% 18%)", "hsl(28 22% 92%)", "hsl(25 14% 41%)", "hsl(28 22% 89%)", "hsl(25 35% 18%)", "hsl(0 72% 51%)", "hsl(28 18% 83%)", "hsl(28 22% 55%)", "hsl(20 40% 52%)", "0.5rem", "hsl(30 25% 95%)", "hsl(25 35% 18%)", "hsl(20 40% 46%)", "hsl(0 0% 100%)", "hsl(28 22% 89%)", "hsl(25 35% 18%)", "hsl(28 18% 83%)", "hsl(20 40% 52%)"),
    dark: v("hsl(25 25% 9%)", "hsl(28 22% 88%)", "hsl(25 22% 12%)", "hsl(28 22% 88%)", "hsl(20 45% 46%)", "hsl(0 0% 100%)", "hsl(26 18% 17%)", "hsl(28 22% 88%)", "hsl(26 18% 14%)", "hsl(25 12% 55%)", "hsl(26 18% 17%)", "hsl(28 22% 88%)", "hsl(0 72% 51%)", "hsl(26 18% 19%)", "hsl(26 18% 42%)", "hsl(20 45% 55%)", "0.5rem", "hsl(25 22% 10%)", "hsl(28 22% 88%)", "hsl(20 45% 46%)", "hsl(0 0% 100%)", "hsl(26 18% 17%)", "hsl(28 22% 88%)", "hsl(26 18% 15%)", "hsl(20 45% 55%)"),
  },
  {
    id: "nature", label: "Nature",
    light: v("hsl(100 20% 97%)", "hsl(100 35% 14%)", "hsl(0 0% 100%)", "hsl(100 35% 14%)", "hsl(100 40% 32%)", "hsl(0 0% 100%)", "hsl(100 20% 88%)", "hsl(100 35% 14%)", "hsl(100 20% 92%)", "hsl(100 14% 38%)", "hsl(100 20% 88%)", "hsl(100 35% 14%)", "hsl(0 72% 51%)", "hsl(100 16% 82%)", "hsl(100 20% 50%)", "hsl(100 40% 40%)", "0.5rem", "hsl(100 20% 95%)", "hsl(100 35% 14%)", "hsl(100 40% 36%)", "hsl(0 0% 100%)", "hsl(100 20% 88%)", "hsl(100 35% 14%)", "hsl(100 16% 82%)", "hsl(100 40% 40%)"),
    dark: v("hsl(100 30% 6%)", "hsl(100 20% 88%)", "hsl(100 25% 9%)", "hsl(100 20% 88%)", "hsl(100 40% 36%)", "hsl(0 0% 100%)", "hsl(100 20% 14%)", "hsl(100 20% 88%)", "hsl(100 20% 11%)", "hsl(100 12% 54%)", "hsl(100 20% 14%)", "hsl(100 20% 88%)", "hsl(0 72% 51%)", "hsl(100 20% 16%)", "hsl(100 20% 36%)", "hsl(100 40% 40%)", "0.5rem", "hsl(100 25% 7%)", "hsl(100 20% 88%)", "hsl(100 40% 36%)", "hsl(0 0% 100%)", "hsl(100 20% 14%)", "hsl(100 20% 88%)", "hsl(100 20% 12%)", "hsl(100 40% 40%)"),
  },
  {
    id: "vintage-paper", label: "Vintage",
    light: v("hsl(40 40% 94%)", "hsl(30 30% 22%)", "hsl(40 35% 98%)", "hsl(30 30% 22%)", "hsl(25 60% 36%)", "hsl(40 40% 96%)", "hsl(38 28% 84%)", "hsl(30 30% 22%)", "hsl(38 28% 88%)", "hsl(30 18% 38%)", "hsl(38 28% 84%)", "hsl(30 30% 22%)", "hsl(0 72% 51%)", "hsl(38 22% 78%)", "hsl(38 28% 49%)", "hsl(25 60% 45%)", "0.25rem", "hsl(40 40% 92%)", "hsl(30 30% 22%)", "hsl(25 60% 41%)", "hsl(40 40% 96%)", "hsl(38 28% 84%)", "hsl(30 30% 22%)", "hsl(38 22% 78%)", "hsl(25 60% 45%)"),
    dark: v("hsl(30 20% 12%)", "hsl(38 28% 82%)", "hsl(30 18% 15%)", "hsl(38 28% 82%)", "hsl(25 55% 42%)", "hsl(38 28% 97%)", "hsl(30 14% 21%)", "hsl(38 28% 82%)", "hsl(30 14% 17%)", "hsl(30 14% 57%)", "hsl(30 14% 21%)", "hsl(38 28% 82%)", "hsl(0 72% 51%)", "hsl(30 14% 23%)", "hsl(30 14% 44%)", "hsl(25 55% 52%)", "0.25rem", "hsl(30 18% 13%)", "hsl(38 28% 82%)", "hsl(25 55% 40%)", "hsl(38 28% 93%)", "hsl(30 14% 21%)", "hsl(38 28% 82%)", "hsl(30 14% 19%)", "hsl(25 55% 52%)"),
  },

  // ── Dark / Special ────────────────────────────────────────────────────────
  {
    id: "cosmic-night", label: "Cosmic Night",
    light: v("hsl(244 30% 97%)", "hsl(244 40% 16%)", "hsl(0 0% 100%)", "hsl(244 40% 16%)", "hsl(244 66% 58%)", "hsl(0 0% 100%)", "hsl(244 30% 89%)", "hsl(244 40% 16%)", "hsl(244 30% 92%)", "hsl(244 14% 44%)", "hsl(244 30% 89%)", "hsl(244 40% 16%)", "hsl(0 72% 51%)", "hsl(244 25% 83%)", "hsl(244 30% 64%)", "hsl(244 66% 58%)", "0.5rem", "hsl(244 30% 95%)", "hsl(244 40% 16%)", "hsl(244 66% 58%)", "hsl(0 0% 100%)", "hsl(244 30% 89%)", "hsl(244 40% 16%)", "hsl(244 25% 83%)", "hsl(244 66% 58%)"),
    dark: v("hsl(244 40% 6%)", "hsl(244 30% 89%)", "hsl(244 36% 10%)", "hsl(244 30% 89%)", "hsl(244 66% 58%)", "hsl(0 0% 100%)", "hsl(244 28% 16%)", "hsl(244 30% 89%)", "hsl(244 28% 12%)", "hsl(244 14% 56%)", "hsl(244 28% 16%)", "hsl(244 30% 89%)", "hsl(0 72% 51%)", "hsl(244 28% 18%)", "hsl(244 28% 49%)", "hsl(244 66% 58%)", "0.5rem", "hsl(244 36% 8%)", "hsl(244 30% 89%)", "hsl(244 66% 58%)", "hsl(0 0% 100%)", "hsl(244 28% 16%)", "hsl(244 30% 89%)", "hsl(244 28% 14%)", "hsl(244 66% 58%)"),
  },
  {
    id: "catppuccin", label: "Catppuccin",
    light: v("hsl(220 23% 95%)", "hsl(234 16% 35%)", "hsl(220 23% 98%)", "hsl(234 16% 35%)", "hsl(266 85% 58%)", "hsl(0 0% 100%)", "hsl(223 16% 86%)", "hsl(234 16% 35%)", "hsl(223 16% 90%)", "hsl(233 10% 42%)", "hsl(223 16% 86%)", "hsl(234 16% 35%)", "hsl(347 87% 44%)", "hsl(223 14% 80%)", "hsl(223 16% 57%)", "hsl(266 85% 58%)", "0.5rem", "hsl(220 23% 93%)", "hsl(234 16% 35%)", "hsl(266 85% 58%)", "hsl(0 0% 100%)", "hsl(223 16% 86%)", "hsl(234 16% 35%)", "hsl(223 14% 80%)", "hsl(266 85% 58%)"),
    dark: v("hsl(232 23% 18%)", "hsl(226 64% 88%)", "hsl(232 23% 21%)", "hsl(226 64% 88%)", "hsl(267 84% 81%)", "hsl(232 23% 14%)", "hsl(233 19% 26%)", "hsl(226 64% 88%)", "hsl(233 19% 23%)", "hsl(227 35% 67%)", "hsl(233 19% 26%)", "hsl(226 64% 88%)", "hsl(343 81% 75%)", "hsl(233 19% 27%)", "hsl(233 19% 53%)", "hsl(267 84% 81%)", "0.5rem", "hsl(232 23% 17%)", "hsl(226 64% 88%)", "hsl(267 84% 81%)", "hsl(232 23% 14%)", "hsl(233 19% 26%)", "hsl(226 64% 88%)", "hsl(233 19% 24%)", "hsl(267 84% 81%)", "hsl(0 0% 25%)"),
  },
  {
    id: "midnight-bloom", label: "Midnight",
    light: v("hsl(260 20% 97%)", "hsl(260 30% 16%)", "hsl(0 0% 100%)", "hsl(260 30% 16%)", "hsl(260 60% 55%)", "hsl(0 0% 100%)", "hsl(260 18% 89%)", "hsl(260 30% 16%)", "hsl(260 18% 92%)", "hsl(260 12% 44%)", "hsl(260 18% 89%)", "hsl(260 30% 16%)", "hsl(0 72% 51%)", "hsl(260 15% 83%)", "hsl(260 18% 60%)", "hsl(260 60% 55%)", "0.5rem", "hsl(260 20% 95%)", "hsl(260 30% 16%)", "hsl(260 60% 55%)", "hsl(0 0% 100%)", "hsl(260 18% 89%)", "hsl(260 30% 16%)", "hsl(260 15% 83%)", "hsl(260 60% 55%)"),
    dark: v("hsl(260 30% 6%)", "hsl(260 18% 89%)", "hsl(260 25% 10%)", "hsl(260 18% 89%)", "hsl(260 60% 55%)", "hsl(0 0% 100%)", "hsl(260 20% 16%)", "hsl(260 18% 89%)", "hsl(260 20% 12%)", "hsl(260 12% 55%)", "hsl(260 20% 16%)", "hsl(260 18% 89%)", "hsl(0 72% 51%)", "hsl(260 20% 18%)", "hsl(260 20% 46%)", "hsl(260 60% 55%)", "0.5rem", "hsl(260 25% 8%)", "hsl(260 18% 89%)", "hsl(260 60% 55%)", "hsl(0 0% 100%)", "hsl(260 20% 16%)", "hsl(260 18% 89%)", "hsl(260 20% 14%)", "hsl(260 60% 55%)"),
  },
  {
    id: "neo-brutalism", label: "Neo Brutal",
    light: v("hsl(55 100% 95%)", "hsl(0 0% 0%)", "hsl(55 100% 98%)", "hsl(0 0% 0%)", "hsl(0 0% 0%)", "hsl(55 100% 95%)", "hsl(55 80% 82%)", "hsl(0 0% 0%)", "hsl(55 80% 88%)", "hsl(0 0% 30%)", "hsl(55 80% 82%)", "hsl(0 0% 0%)", "hsl(0 85% 55%)", "hsl(0 0% 0%)", "hsl(55 80% 35%)", "hsl(0 0% 0%)", "0rem", "hsl(55 100% 93%)", "hsl(0 0% 0%)", "hsl(0 0% 0%)", "hsl(55 100% 95%)", "hsl(55 80% 82%)", "hsl(0 0% 0%)", "hsl(0 0% 0%)", "hsl(0 0% 0%)", "hsl(0 0% 6%)"),
    dark: v("hsl(0 0% 5%)", "hsl(55 100% 80%)", "hsl(0 0% 8%)", "hsl(55 100% 80%)", "hsl(55 100% 68%)", "hsl(0 0% 5%)", "hsl(0 0% 14%)", "hsl(55 100% 80%)", "hsl(0 0% 11%)", "hsl(55 70% 55%)", "hsl(0 0% 14%)", "hsl(55 100% 80%)", "hsl(0 85% 55%)", "hsl(0 0% 0%)", "hsl(0 0% 39%)", "hsl(55 100% 68%)", "0rem", "hsl(0 0% 6%)", "hsl(55 100% 80%)", "hsl(55 100% 68%)", "hsl(0 0% 5%)", "hsl(0 0% 14%)", "hsl(55 100% 80%)", "hsl(0 0% 0%)", "hsl(55 100% 68%)", "hsl(0 0% 6%)"),
  },
  {
    id: "cyberpunk", label: "Cyberpunk",
    light: v("hsl(180 10% 97%)", "hsl(180 80% 10%)", "hsl(0 0% 100%)", "hsl(180 80% 10%)", "hsl(180 100% 23%)", "hsl(0 0% 100%)", "hsl(180 20% 88%)", "hsl(180 80% 10%)", "hsl(180 20% 92%)", "hsl(180 14% 38%)", "hsl(180 20% 88%)", "hsl(180 80% 10%)", "hsl(330 100% 50%)", "hsl(180 20% 82%)", "hsl(180 20% 49%)", "hsl(180 100% 31%)", "0rem", "hsl(180 10% 95%)", "hsl(180 80% 10%)", "hsl(180 100% 25%)", "hsl(0 0% 100%)", "hsl(180 20% 88%)", "hsl(180 80% 10%)", "hsl(180 20% 82%)", "hsl(180 100% 35%)", "hsl(0 0% 11%)"),
    dark: v("hsl(220 30% 5%)", "hsl(180 100% 70%)", "hsl(220 25% 8%)", "hsl(180 100% 70%)", "hsl(180 100% 50%)", "hsl(220 30% 5%)", "hsl(210 25% 13%)", "hsl(180 100% 70%)", "hsl(210 25% 10%)", "hsl(180 50% 50%)", "hsl(210 25% 13%)", "hsl(180 100% 70%)", "hsl(330 100% 50%)", "hsl(210 40% 17%)", "hsl(210 25% 40%)", "hsl(180 100% 50%)", "0rem", "hsl(220 25% 6%)", "hsl(180 100% 70%)", "hsl(180 100% 50%)", "hsl(220 30% 5%)", "hsl(210 25% 13%)", "hsl(180 100% 70%)", "hsl(210 40% 15%)", "hsl(180 100% 50%)", "hsl(0 0% 11%)"),
  },
  {
    id: "bubblegum", label: "Bubblegum",
    light: v("hsl(340 100% 97%)", "hsl(320 50% 25%)", "hsl(0 0% 100%)", "hsl(320 50% 25%)", "hsl(340 82% 47%)", "hsl(0 0% 100%)", "hsl(340 60% 89%)", "hsl(320 50% 25%)", "hsl(340 60% 93%)", "hsl(320 22% 44%)", "hsl(340 60% 89%)", "hsl(320 50% 25%)", "hsl(0 72% 51%)", "hsl(340 50% 84%)", "hsl(340 60% 63%)", "hsl(340 82% 63%)", "1rem", "hsl(340 100% 95%)", "hsl(320 50% 25%)", "hsl(340 82% 49%)", "hsl(0 0% 100%)", "hsl(340 60% 89%)", "hsl(320 50% 25%)", "hsl(340 50% 84%)", "hsl(340 82% 70%)"),
    dark: v("hsl(320 40% 10%)", "hsl(340 60% 90%)", "hsl(320 35% 13%)", "hsl(340 60% 90%)", "hsl(340 82% 49%)", "hsl(0 0% 100%)", "hsl(320 30% 19%)", "hsl(340 60% 90%)", "hsl(320 30% 16%)", "hsl(320 20% 62%)", "hsl(320 30% 19%)", "hsl(340 60% 90%)", "hsl(0 72% 51%)", "hsl(320 30% 20%)", "hsl(320 30% 46%)", "hsl(340 82% 70%)", "1rem", "hsl(320 35% 11%)", "hsl(340 60% 90%)", "hsl(340 82% 49%)", "hsl(0 0% 100%)", "hsl(320 30% 19%)", "hsl(340 60% 90%)", "hsl(320 30% 17%)", "hsl(340 82% 70%)"),
  },
  {
    id: "solar-dusk", label: "Solar Dusk",
    light: v("hsl(35 60% 97%)", "hsl(25 55% 18%)", "hsl(0 0% 100%)", "hsl(25 55% 18%)", "hsl(15 90% 41%)", "hsl(0 0% 100%)", "hsl(30 45% 88%)", "hsl(25 55% 18%)", "hsl(30 45% 92%)", "hsl(25 22% 41%)", "hsl(30 45% 88%)", "hsl(25 55% 18%)", "hsl(0 72% 51%)", "hsl(30 38% 82%)", "hsl(30 45% 52%)", "hsl(15 90% 55%)", "0.5rem", "hsl(35 60% 95%)", "hsl(25 55% 18%)", "hsl(15 90% 44%)", "hsl(0 0% 100%)", "hsl(30 45% 88%)", "hsl(25 55% 18%)", "hsl(30 38% 82%)", "hsl(15 90% 55%)"),
    dark: v("hsl(25 40% 8%)", "hsl(30 45% 87%)", "hsl(25 35% 11%)", "hsl(30 45% 87%)", "hsl(15 90% 44%)", "hsl(0 0% 100%)", "hsl(24 25% 16%)", "hsl(30 45% 87%)", "hsl(24 25% 13%)", "hsl(25 20% 57%)", "hsl(24 25% 16%)", "hsl(30 45% 87%)", "hsl(0 72% 51%)", "hsl(24 25% 18%)", "hsl(24 25% 41%)", "hsl(15 90% 55%)", "0.5rem", "hsl(25 35% 9%)", "hsl(30 45% 87%)", "hsl(15 90% 44%)", "hsl(0 0% 100%)", "hsl(24 25% 16%)", "hsl(30 45% 87%)", "hsl(24 25% 14%)", "hsl(15 90% 55%)"),
  },
  {
    id: "pastel-dreams", label: "Pastel",
    light: v("hsl(300 30% 98%)", "hsl(280 25% 22%)", "hsl(0 0% 100%)", "hsl(280 25% 22%)", "hsl(300 60% 44%)", "hsl(0 0% 100%)", "hsl(300 25% 90%)", "hsl(280 25% 22%)", "hsl(300 25% 93%)", "hsl(280 14% 45%)", "hsl(300 25% 90%)", "hsl(280 25% 22%)", "hsl(0 72% 51%)", "hsl(300 20% 84%)", "hsl(300 25% 60%)", "hsl(300 60% 61%)", "0.75rem", "hsl(300 30% 96%)", "hsl(280 25% 22%)", "hsl(300 60% 48%)", "hsl(0 0% 100%)", "hsl(300 25% 90%)", "hsl(280 25% 22%)", "hsl(300 20% 84%)", "hsl(300 60% 72%)"),
    dark: v("hsl(280 25% 8%)", "hsl(300 25% 89%)", "hsl(280 22% 11%)", "hsl(300 25% 89%)", "hsl(300 60% 48%)", "hsl(0 0% 100%)", "hsl(285 20% 17%)", "hsl(300 25% 89%)", "hsl(285 20% 14%)", "hsl(280 14% 58%)", "hsl(285 20% 17%)", "hsl(300 25% 89%)", "hsl(0 72% 51%)", "hsl(285 20% 18%)", "hsl(285 20% 44%)", "hsl(300 60% 72%)", "0.75rem", "hsl(280 22% 9%)", "hsl(300 25% 89%)", "hsl(300 60% 48%)", "hsl(0 0% 100%)", "hsl(285 20% 17%)", "hsl(300 25% 89%)", "hsl(285 20% 15%)", "hsl(300 60% 72%)"),
  },

  // ── Nord-inspired ─────────────────────────────────────────────────────────
  {
    // Ohne "Inter": die Beschriftung war schon vorher unwahr — das Theme hatte
    // nie einen Ladeweg fuer Inter, `applyFont` verlangte fontSans UND fontUrl
    // und liess das Theme deshalb immer in Geist laufen.
    id: "nord", label: "Nord",
    light: v("hsl(220 16% 96%)", "hsl(222 47% 20%)", "hsl(218 27% 98%)", "hsl(222 47% 20%)", "hsl(213 32% 41%)", "hsl(218 27% 98%)", "hsl(220 16% 87%)", "hsl(222 47% 20%)", "hsl(220 16% 91%)", "hsl(220 14% 42%)", "hsl(220 16% 87%)", "hsl(222 47% 20%)", "hsl(354 42% 56%)", "hsl(220 14% 82%)", "hsl(220 16% 57%)", "hsl(213 32% 52%)", "0.5rem", "hsl(220 16% 94%)", "hsl(222 47% 20%)", "hsl(213 32% 47%)", "hsl(218 27% 98%)", "hsl(220 16% 87%)", "hsl(222 47% 20%)", "hsl(220 14% 82%)", "hsl(213 32% 52%)", "hsl(0 0% 7%)"),
    dark: v("hsl(220 16% 16%)", "hsl(218 27% 88%)", "hsl(222 16% 21%)", "hsl(218 27% 88%)", "hsl(213 40% 48%)", "hsl(218 27% 98%)", "hsl(220 14% 26%)", "hsl(218 27% 88%)", "hsl(220 14% 22%)", "hsl(218 14% 64%)", "hsl(220 14% 26%)", "hsl(218 27% 88%)", "hsl(354 42% 56%)", "hsl(220 14% 27%)", "hsl(220 14% 52%)", "hsl(213 32% 52%)", "0.5rem", "hsl(220 16% 14%)", "hsl(218 27% 88%)", "hsl(213 32% 47%)", "hsl(218 27% 98%)", "hsl(220 14% 26%)", "hsl(218 27% 88%)", "hsl(220 14% 24%)", "hsl(213 32% 52%)", "hsl(0 0% 7%)"),
  },
  {
    id: "slate", label: "Slate",
    light: v("hsl(210 40% 98%)", "hsl(222 47% 11%)", "hsl(0 0% 100%)", "hsl(222 47% 11%)", "hsl(222 47% 11%)", "hsl(0 0% 100%)", "hsl(210 40% 90%)", "hsl(222 47% 11%)", "hsl(210 40% 94%)", "hsl(215 16% 44%)", "hsl(210 40% 90%)", "hsl(222 47% 11%)", "hsl(0 72% 51%)", "hsl(214 32% 84%)", "hsl(210 40% 58%)", "hsl(222 47% 11%)", "0.5rem", "hsl(210 40% 96%)", "hsl(222 47% 11%)", "hsl(222 47% 11%)", "hsl(0 0% 100%)", "hsl(210 40% 90%)", "hsl(222 47% 11%)", "hsl(214 32% 84%)", "hsl(222 47% 11%)"),
    dark: v("hsl(222 47% 5%)", "hsl(213 31% 91%)", "hsl(222 47% 8%)", "hsl(213 31% 91%)", "hsl(213 31% 91%)", "hsl(222 47% 5%)", "hsl(215 28% 17%)", "hsl(213 31% 91%)", "hsl(215 28% 13%)", "hsl(215 20% 55%)", "hsl(215 28% 17%)", "hsl(213 31% 91%)", "hsl(0 72% 51%)", "hsl(215 28% 18%)", "hsl(215 28% 41%)", "hsl(215 20% 40%)", "0.5rem", "hsl(222 47% 6%)", "hsl(213 31% 91%)", "hsl(213 31% 91%)", "hsl(222 47% 5%)", "hsl(215 28% 17%)", "hsl(213 31% 91%)", "hsl(215 28% 15%)", "hsl(215 20% 40%)"),
  },
  {
    id: "zinc", label: "Zinc",
    light: v("hsl(0 0% 98%)", "hsl(240 10% 4%)", "hsl(0 0% 100%)", "hsl(240 10% 4%)", "hsl(240 6% 10%)", "hsl(0 0% 98%)", "hsl(240 5% 90%)", "hsl(240 6% 10%)", "hsl(240 5% 94%)", "hsl(240 4% 44%)", "hsl(240 5% 90%)", "hsl(240 6% 10%)", "hsl(0 72% 51%)", "hsl(240 6% 84%)", "hsl(240 5% 58%)", "hsl(240 6% 10%)", "0.5rem", "hsl(0 0% 97%)", "hsl(240 10% 4%)", "hsl(240 6% 10%)", "hsl(0 0% 98%)", "hsl(240 5% 90%)", "hsl(240 6% 10%)", "hsl(240 6% 84%)", "hsl(240 6% 10%)"),
    dark: v("hsl(240 10% 4%)", "hsl(0 0% 98%)", "hsl(240 10% 6%)", "hsl(0 0% 98%)", "hsl(0 0% 98%)", "hsl(240 10% 4%)", "hsl(240 4% 16%)", "hsl(0 0% 98%)", "hsl(240 4% 12%)", "hsl(240 5% 55%)", "hsl(240 4% 16%)", "hsl(0 0% 98%)", "hsl(0 72% 51%)", "hsl(240 4% 17%)", "hsl(240 4% 39%)", "hsl(240 5% 39%)", "0.5rem", "hsl(240 10% 5%)", "hsl(0 0% 98%)", "hsl(0 0% 98%)", "hsl(240 10% 4%)", "hsl(240 4% 16%)", "hsl(0 0% 98%)", "hsl(240 4% 14%)", "hsl(240 5% 38%)"),
  },
  {
    id: "stone", label: "Stone",
    light: v("hsl(60 9% 98%)", "hsl(20 14% 4%)", "hsl(0 0% 100%)", "hsl(20 14% 4%)", "hsl(24 10% 10%)", "hsl(60 9% 98%)", "hsl(60 5% 90%)", "hsl(24 10% 10%)", "hsl(60 5% 94%)", "hsl(25 6% 42%)", "hsl(60 5% 90%)", "hsl(24 10% 10%)", "hsl(0 72% 51%)", "hsl(20 6% 84%)", "hsl(60 5% 55%)", "hsl(24 10% 10%)", "0.5rem", "hsl(60 9% 96%)", "hsl(20 14% 4%)", "hsl(24 10% 10%)", "hsl(60 9% 98%)", "hsl(60 5% 90%)", "hsl(24 10% 10%)", "hsl(20 6% 84%)", "hsl(24 10% 10%)"),
    dark: v("hsl(20 14% 4%)", "hsl(60 9% 98%)", "hsl(24 10% 7%)", "hsl(60 9% 98%)", "hsl(60 9% 98%)", "hsl(20 14% 4%)", "hsl(12 6% 17%)", "hsl(60 9% 98%)", "hsl(12 6% 12%)", "hsl(24 6% 54%)", "hsl(12 6% 17%)", "hsl(60 9% 98%)", "hsl(0 72% 51%)", "hsl(12 6% 18%)", "hsl(12 6% 39%)", "hsl(24 6% 38%)", "0.5rem", "hsl(24 10% 5%)", "hsl(60 9% 98%)", "hsl(60 9% 98%)", "hsl(20 14% 4%)", "hsl(12 6% 17%)", "hsl(60 9% 98%)", "hsl(12 6% 15%)", "hsl(24 6% 37%)"),
  },
  {
    id: "ocean", label: "Ocean",
    light: v("hsl(200 30% 97%)", "hsl(205 60% 15%)", "hsl(0 0% 100%)", "hsl(205 60% 15%)", "hsl(204 70% 38%)", "hsl(0 0% 100%)", "hsl(202 25% 88%)", "hsl(205 60% 15%)", "hsl(202 25% 92%)", "hsl(203 18% 41%)", "hsl(202 25% 88%)", "hsl(205 60% 15%)", "hsl(0 72% 51%)", "hsl(202 22% 82%)", "hsl(202 25% 55%)", "hsl(204 70% 45%)", "0.5rem", "hsl(200 30% 95%)", "hsl(205 60% 15%)", "hsl(204 70% 42%)", "hsl(0 0% 100%)", "hsl(202 25% 88%)", "hsl(205 60% 15%)", "hsl(202 22% 82%)", "hsl(204 70% 45%)"),
    dark: v("hsl(205 60% 5%)", "hsl(202 25% 89%)", "hsl(205 55% 8%)", "hsl(202 25% 89%)", "hsl(204 70% 42%)", "hsl(0 0% 100%)", "hsl(203 30% 14%)", "hsl(202 25% 89%)", "hsl(203 30% 11%)", "hsl(203 18% 54%)", "hsl(203 30% 14%)", "hsl(202 25% 89%)", "hsl(0 72% 51%)", "hsl(203 30% 16%)", "hsl(203 30% 39%)", "hsl(204 70% 45%)", "0.5rem", "hsl(205 55% 6%)", "hsl(202 25% 89%)", "hsl(204 70% 42%)", "hsl(0 0% 100%)", "hsl(203 30% 14%)", "hsl(202 25% 89%)", "hsl(203 30% 12%)", "hsl(204 70% 45%)"),
  },
  {
    id: "forest", label: "Forest",
    light: v("hsl(140 20% 97%)", "hsl(138 45% 14%)", "hsl(0 0% 100%)", "hsl(138 45% 14%)", "hsl(138 55% 30%)", "hsl(0 0% 100%)", "hsl(140 18% 88%)", "hsl(138 45% 14%)", "hsl(140 18% 92%)", "hsl(138 12% 39%)", "hsl(140 18% 88%)", "hsl(138 45% 14%)", "hsl(0 72% 51%)", "hsl(140 14% 82%)", "hsl(140 18% 51%)", "hsl(138 55% 36%)", "0.5rem", "hsl(140 20% 95%)", "hsl(138 45% 14%)", "hsl(138 55% 34%)", "hsl(0 0% 100%)", "hsl(140 18% 88%)", "hsl(138 45% 14%)", "hsl(140 14% 82%)", "hsl(138 55% 36%)"),
    dark: v("hsl(138 45% 5%)", "hsl(140 18% 88%)", "hsl(138 38% 8%)", "hsl(140 18% 88%)", "hsl(138 55% 34%)", "hsl(0 0% 100%)", "hsl(139 25% 14%)", "hsl(140 18% 88%)", "hsl(139 25% 11%)", "hsl(138 12% 54%)", "hsl(139 25% 14%)", "hsl(140 18% 88%)", "hsl(0 72% 51%)", "hsl(139 25% 16%)", "hsl(139 25% 35%)", "hsl(138 55% 36%)", "0.5rem", "hsl(138 38% 6%)", "hsl(140 18% 88%)", "hsl(138 55% 34%)", "hsl(0 0% 100%)", "hsl(139 25% 14%)", "hsl(140 18% 88%)", "hsl(139 25% 12%)", "hsl(138 55% 36%)"),
  },
  {
    id: "arctic", label: "Arctic",
    light: v("hsl(195 50% 97%)", "hsl(200 60% 14%)", "hsl(0 0% 100%)", "hsl(200 60% 14%)", "hsl(196 80% 32%)", "hsl(0 0% 100%)", "hsl(196 30% 88%)", "hsl(200 60% 14%)", "hsl(196 30% 92%)", "hsl(198 18% 40%)", "hsl(196 30% 88%)", "hsl(200 60% 14%)", "hsl(0 72% 51%)", "hsl(196 25% 82%)", "hsl(196 30% 52%)", "hsl(196 80% 44%)", "0.5rem", "hsl(195 50% 95%)", "hsl(200 60% 14%)", "hsl(196 80% 36%)", "hsl(0 0% 100%)", "hsl(196 30% 88%)", "hsl(200 60% 14%)", "hsl(196 25% 82%)", "hsl(196 80% 45%)"),
    dark: v("hsl(200 60% 5%)", "hsl(196 30% 89%)", "hsl(200 50% 8%)", "hsl(196 30% 89%)", "hsl(196 80% 36%)", "hsl(0 0% 100%)", "hsl(198 35% 14%)", "hsl(196 30% 89%)", "hsl(198 35% 11%)", "hsl(198 18% 54%)", "hsl(198 35% 14%)", "hsl(196 30% 89%)", "hsl(0 72% 51%)", "hsl(198 35% 16%)", "hsl(198 35% 37%)", "hsl(196 80% 45%)", "0.5rem", "hsl(200 50% 6%)", "hsl(196 30% 89%)", "hsl(196 80% 36%)", "hsl(0 0% 100%)", "hsl(198 35% 14%)", "hsl(196 30% 89%)", "hsl(198 35% 12%)", "hsl(196 80% 45%)"),
  },
  {
    id: "sunset", label: "Sunset",
    light: v("hsl(20 40% 97%)", "hsl(15 60% 16%)", "hsl(0 0% 100%)", "hsl(15 60% 16%)", "hsl(10 80% 44%)", "hsl(0 0% 100%)", "hsl(18 35% 88%)", "hsl(15 60% 16%)", "hsl(18 35% 92%)", "hsl(16 20% 42%)", "hsl(18 35% 88%)", "hsl(15 60% 16%)", "hsl(0 72% 51%)", "hsl(18 28% 82%)", "hsl(18 35% 57%)", "hsl(10 80% 58%)", "0.5rem", "hsl(20 40% 95%)", "hsl(15 60% 16%)", "hsl(10 80% 48%)", "hsl(0 0% 100%)", "hsl(18 35% 88%)", "hsl(15 60% 16%)", "hsl(18 28% 82%)", "hsl(10 80% 58%)"),
    dark: v("hsl(15 50% 7%)", "hsl(18 35% 88%)", "hsl(15 44% 10%)", "hsl(18 35% 88%)", "hsl(10 80% 48%)", "hsl(0 0% 100%)", "hsl(16 28% 16%)", "hsl(18 35% 88%)", "hsl(16 28% 13%)", "hsl(16 18% 56%)", "hsl(16 28% 16%)", "hsl(18 35% 88%)", "hsl(0 72% 51%)", "hsl(16 28% 18%)", "hsl(16 28% 42%)", "hsl(10 80% 58%)", "0.5rem", "hsl(15 44% 8%)", "hsl(18 35% 88%)", "hsl(10 80% 48%)", "hsl(0 0% 100%)", "hsl(16 28% 16%)", "hsl(18 35% 88%)", "hsl(16 28% 14%)", "hsl(10 80% 58%)"),
  },
];

// `destructive`/`destructive-foreground` werden hier NACHTRAEGLICH geloest, nicht
// innerhalb von `v()`: `v()` bedient hell UND dunkel mit derselben Funktion, die
// beiden Loeser aber sind Gegenrichtungen (dunkel hebt die Helligkeit, hell senkt
// sie) und muessen deshalb getrennt bleiben. Deshalb hier, EINMAL pro Modus,
// angewandt — kuenftige Themes bekommen beides automatisch mit.
export const themes: Theme[] = themesRoh.map((t) => ({
  ...t,
  light: { ...t.light, ...destructiveHellLoesen(t.light) },
  dark: { ...t.dark, ...destructiveDunkelLoesen(t.dark) },
}));

export function getTheme(id: string): Theme {
  return themes.find(t => t.id === id) ?? themes[0];
}
